import React, { useEffect, useImperativeHandle, useMemo, useState } from "react";
import classNames from "classnames";
import { useQueryClient } from "@tanstack/react-query";
import { Row, Col, Form, FormGroup, Label, Input, Card, CardBody, CardTitle, CardHeader, Table, Button, TabContent, TabPane, Nav, NavItem, NavLink, Popover, PopoverHeader, PopoverBody, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { getActiveColorStyle } from "utils/colorUtils";
import { useGetWorksheetDetail } from "api/worksheet/service";
import { formatPriceWithDollar, toNumber, formatPercentage } from "utils/currency";
import { formatDateForDisplay, formatDateForAPI } from "utils/dateUtils";
import ReactDatetime from "components/CustomDatePicker";
import moment from "moment";
import { getSelectStyles } from "utils/selectStyles";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import Select from "components/Select";
import { useUpdateWorksheet } from "api/worksheet/service";
import AddVehicle from "./AddVehicle";
import { useGetInHouseAprs } from "api/in-house-aprs/service";
import DollarInput from "components/DollarInput";
import { useNotification } from "contexts/NotificationContext";
import SecureMediaImg from "components/SecureMediaImg";

const toDollarFieldValue = (value) => {
  if (value === undefined || value === null || value === "") return "$0.00";
  if (typeof value === "number") return formatPriceWithDollar(value);
  if (typeof value === "string" && value.trim().startsWith("$")) return value;
  return formatPriceWithDollar(value);
};

const getTaxFeeRowKey = (row) =>
  row?.local_id || (row?.id != null ? String(row.id) : null) || row?.documentId || null;

const taxAndFeeDisplayRowToSnapshot = (row) => {
  const snapshot = {
    source_id: row?.source_id ?? row?.id ?? null,
    type: row?.type,
    fee_type: row?.fee_type,
    category: row?.category,
    description: row?.description,
    amount: toNumber(row?.amount),
    tax_rate: row?.tax_rate != null ? toNumber(row.tax_rate) : 0,
    tax_variable: row?.tax_variable,
    calc_type: row?.calc_type,
    indicator: row?.indicator,
    fixed: row?.fixed,
    beginning_range: row?.beginning_range,
    end_range: row?.end_range,
    can_edit_at_time_of_sale: row?.can_edit_at_time_of_sale,
  };
  if (row?.local_id) snapshot.local_id = row.local_id;
  return snapshot;
};

const getWorksheetTaxFeeSnapshotRows = (worksheetData) => {
  if (Array.isArray(worksheetData?.tax_and_fees)) {
    return worksheetData.tax_and_fees.map(taxAndFeeDisplayRowToSnapshot);
  }
  return (worksheetData?.taxAndFee || []).map(taxAndFeeDisplayRowToSnapshot);
};

/** Sales location catalog — fixed accessory definitions (selection only). */
const getCatalogAccessoriesFromWorksheet = (worksheetData) => {
  const raw =
    worksheetData?.sales_location?.accessories ??
    worksheetData?.inventory?.location?.sales_location?.accessories ??
    [];
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return list.filter((item) => item?.is_active !== false);
};

const parseSelectedAccessories = (raw) => {
  if (raw == null) return [];
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
      return parsed ? [parsed] : [];
    } catch {
      return [];
    }
  }
  return Array.isArray(raw) ? raw.filter(Boolean) : [raw];
};

const resolveCatalogAccessoryId = (item) => {
  if (item?.accessory_id != null) return item.accessory_id;
  if (typeof item?.accessories === "number") return item.accessories;
  if (item?.accessories?.id != null) return item.accessories.id;
  return item?.id ?? null;
};

const getCatalogAccessoryKey = (item) =>
  resolveCatalogAccessoryId(item) ?? item?.documentId ?? null;

const isSameCatalogAccessory = (catalogItem, selectedItem) => {
  if (!catalogItem || !selectedItem) return false;
  const catalogId = catalogItem.id;
  const selectedCatalogId = resolveCatalogAccessoryId(selectedItem);
  if (catalogId != null && selectedCatalogId != null && String(catalogId) === String(selectedCatalogId)) {
    return true;
  }
  if (
    catalogItem.documentId &&
    selectedItem.documentId &&
    catalogItem.documentId === selectedItem.documentId
  ) {
    return true;
  }
  return false;
};

/** Worksheet line from API `selected_accessories` JSON. */
const mapSelectedAccessoryFromApi = (item) => {
  const catalogId = resolveCatalogAccessoryId(item);
  return {
    accessories: catalogId,
    accessory_id: catalogId,
    documentId: item.documentId ?? item.accessories?.documentId,
    name: item.display_name || item.name || item.accessories?.display_name || "",
    display_name: item.display_name || item.name || item.accessories?.display_name || "",
    description: item.description ?? "",
    price: toDollarFieldValue(item.price),
    cost: toDollarFieldValue(item.cost),
  };
};

/** New row when user picks from catalog dropdown (values edited on worksheet). */
const mapCatalogPickToSelectedLine = (catalogItem) => ({
  accessories: catalogItem.id,
  accessory_id: catalogItem.id,
  documentId: catalogItem.documentId,
  name: catalogItem.display_name || catalogItem.name || "",
  display_name: catalogItem.display_name || catalogItem.name || "",
  description: "",
  price: toDollarFieldValue(0),
  cost: toDollarFieldValue(0),
});

const mapSelectedAccessoriesForApi = (items) =>
  (items || []).map((item) => {
    const catalogId = resolveCatalogAccessoryId(item);
    return {
      accessories: catalogId,
      ...(item.documentId ? { documentId: item.documentId } : {}),
      display_name: item.display_name || item.name || "",
      description: item.description ?? "",
      price: toNumber(item.price),
      cost: toNumber(item.cost),
    };
  });

const WorksheetForm = React.forwardRef(({ isLightTheme, activeColor = "blue", initialData = {}, type, id, handleCancelWorksheet, dealId, permission, isTheme2 = false }, ref) => {
  const canEditWorksheet = permission?.deals_worksheets_edit === true;
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("DETAIL");
  const [activeMainTab, setActiveMainTab] = useState("LENDER_FEES");
  const customSelectStyles = useMemo(() => getSelectStyles(activeColorStyle, isLightTheme), [activeColorStyle, isLightTheme]);
  const resolvedSelectStyles = useMemo(
    () =>
      isTheme2
        ? getTheme2SearchFilterSingleSelectStyles(activeColorStyle, true)
        : customSelectStyles,
    [isTheme2, activeColorStyle, customSelectStyles]
  );
  const selectStylesWithPortal = useMemo(
    () =>
      resolvedSelectStyles
        ? {
            ...resolvedSelectStyles,
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }
        : { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
    [resolvedSelectStyles]
  );
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".worksheet-form.worksheet-form--theme2",
            fieldsSelector: ".worksheet-form-fields",
          })
        : "",
    [isTheme2]
  );
  const inputStyle14 = isTheme2
    ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
    : { borderRadius: 0, fontSize: "14px" };
  const inputStyle13 = isTheme2
    ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
    : { borderRadius: 0, fontSize: "13px" };
  const getDateInputProps = (extra = {}) => {
    const { className, ...rest } = extra;
    return {
      className: classNames("form-control", className),
      style: isTheme2
        ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
        : { borderRadius: 0, fontSize: "14px" },
      ...rest,
    };
  };
  const getDateInputProps13 = (extra = {}) => {
    const { className, ...rest } = extra;
    return {
      className: classNames("form-control", className),
      style: isTheme2
        ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
        : { borderRadius: 0, fontSize: "13px" },
      ...rest,
    };
  };
  const [schedules, setSchedules] = useState([{
    id: 1,
    number_of_payments: "",
    desired_payment: formatPriceWithDollar(0),
    frequency: "Monthly",
    first_payment_date: "",
    final_payment_due_on: "",
    final_payment_amount: formatPriceWithDollar(0)
  }]);
  const [selectedAccessories, setSelectedAccessories] = useState([]);
  // Theme-aware text coloring (mirrors Users page behavior)
  const [currentTheme, setCurrentTheme] = useState(() => {
    const darkMode = localStorage.getItem("darkMode");
    return darkMode ? JSON.parse(darkMode) : true;
  });
  const { showNotification } = useNotification();
  const updateWorksheetMutation = useUpdateWorksheet();
  const { data: worksheetData } = useGetWorksheetDetail(id);
  const { data: aprData } = useGetInHouseAprs();

  // Check if worksheet is contracted or contracted_booked - if so, disable all inputs
  const isDisabled = useMemo(() => {
    return ["contracted", "loan_booked"].includes(worksheetData?.worksheet_status);
  }, [worksheetData?.worksheet_status]);


  const getTextColor = () => {
    return currentTheme ? 'white' : 'black';
  };

  const getCardShadowStyle = () => {
    return currentTheme
      ? {}
      : { boxShadow: '0 6px 18px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)' };
  };

  const popoverTheme = useMemo(
    () =>
      currentTheme
        ? {
            bg: "#27293d",
            headerBg: "#27293d",
            text: "#ffffff",
            label: "rgba(255,255,255,0.85)",
            muted: "rgba(255,255,255,0.55)",
            detail: "rgba(255,255,255,0.65)",
            rowBorder: "rgba(255,255,255,0.1)",
            navBorder: "rgba(255,255,255,0.15)",
            navActiveBg: "#1e1e2d",
            navActiveShadow: "0 2px 4px rgba(0,0,0,0.35)",
            modifier: "worksheet-tax-fee-popover--dark",
          }
        : {
            bg: "#ffffff",
            headerBg: "#ffffff",
            text: "#333333",
            label: "#444444",
            muted: "#6c757d",
            detail: "#666666",
            rowBorder: "#f0f0f0",
            navBorder: "#e9ecef",
            navActiveBg: "#ffffff",
            navActiveShadow: "0 2px 4px rgba(0,0,0,0.1)",
            modifier: "worksheet-tax-fee-popover--light",
          },
    [currentTheme]
  );

  const popoverInputStyle = useMemo(
    () => ({ fontSize: 14, color: popoverTheme.text }),
    [popoverTheme.text]
  );

  const popoverInputStyleRight = useMemo(
    () => ({ ...popoverInputStyle, textAlign: "right" }),
    [popoverInputStyle]
  );

  const popoverSelectStyles = useMemo(() => {
    const baseStyles =
      currentTheme || isTheme2
        ? getTheme2SearchFilterSingleSelectStyles(activeColorStyle, true)
        : customSelectStyles;
    return baseStyles
      ? {
          ...baseStyles,
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }
      : { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };
  }, [currentTheme, isTheme2, activeColorStyle, customSelectStyles]);

  const getPopoverNavTabStyle = (isActive) => ({
    cursor: "pointer",
    padding: "8px 0",
    fontSize: "0.8rem",
    fontWeight: 600,
    borderRadius: 0,
    backgroundColor: isActive ? popoverTheme.navActiveBg : "transparent",
    color: isActive ? activeColorStyle.borderColor : popoverTheme.muted,
    boxShadow: isActive ? popoverTheme.navActiveShadow : "none",
  });

  const worksheetTaxFeePopoverCss = `
    .fees-custom-popover .nav-pills,
    .fees-custom-popover .nav-pills .nav-item,
    .fees-custom-popover .nav-pills .nav-link {
      border-radius: 0 !important;
    }
    .fees-custom-popover.worksheet-tax-fee-popover--dark.popover,
    .taxes-custom-popover.worksheet-tax-fee-popover--dark.popover {
      background-color: #27293d !important;
      border-color: rgba(255, 255, 255, 0.12) !important;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45) !important;
    }
    .fees-custom-popover.worksheet-tax-fee-popover--dark .popover-header,
    .taxes-custom-popover.worksheet-tax-fee-popover--dark .popover-header {
      background-color: #27293d !important;
      color: #ffffff !important;
      border-bottom-color: rgba(255, 255, 255, 0.12) !important;
    }
    .fees-custom-popover.worksheet-tax-fee-popover--dark .popover-body,
    .taxes-custom-popover.worksheet-tax-fee-popover--dark .popover-body {
      background-color: #27293d !important;
      color: rgba(255, 255, 255, 0.85) !important;
    }
    .fees-custom-popover.worksheet-tax-fee-popover--dark .form-control,
    .taxes-custom-popover.worksheet-tax-fee-popover--dark .form-control {
      background-color: #1e1e2d !important;
      border-color: rgba(255, 255, 255, 0.15) !important;
      color: #ffffff !important;
    }
    .fees-custom-popover.worksheet-tax-fee-popover--dark .form-control:focus,
    .taxes-custom-popover.worksheet-tax-fee-popover--dark .form-control:focus {
      background-color: #1e1e2d !important;
      border-color: rgba(255, 255, 255, 0.28) !important;
      color: #ffffff !important;
    }
    .fees-custom-popover.worksheet-tax-fee-popover--dark .text-muted,
    .taxes-custom-popover.worksheet-tax-fee-popover--dark .text-muted {
      color: rgba(255, 255, 255, 0.55) !important;
    }
    .fees-custom-popover.worksheet-tax-fee-popover--dark .btn-outline-secondary,
    .taxes-custom-popover.worksheet-tax-fee-popover--dark .btn-outline-secondary {
      color: rgba(255, 255, 255, 0.75) !important;
      border-color: rgba(255, 255, 255, 0.25) !important;
    }
    .fees-custom-popover.worksheet-tax-fee-popover--dark.bs-popover-bottom .arrow::before,
    .fees-custom-popover.worksheet-tax-fee-popover--dark.bs-popover-bottom .popover-arrow::before,
    .taxes-custom-popover.worksheet-tax-fee-popover--dark.bs-popover-bottom .arrow::before,
    .taxes-custom-popover.worksheet-tax-fee-popover--dark.bs-popover-bottom .popover-arrow::before {
      border-bottom-color: #27293d !important;
    }
  `;

  useEffect(() => {
    const handleStorageChange = () => {
      const darkMode = localStorage.getItem("darkMode");
      const newTheme = darkMode ? JSON.parse(darkMode) : true;
      setCurrentTheme(newTheme);
    };

    window.addEventListener('storage', handleStorageChange);

    const interval = setInterval(() => {
      const darkMode = localStorage.getItem("darkMode");
      const newTheme = darkMode ? JSON.parse(darkMode) : true;
      if (newTheme !== currentTheme) {
        setCurrentTheme(newTheme);
      }
    }, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [currentTheme]);

  const [tradeIns, setTradeIns] = useState(() => {
    if (worksheetData?.trade_in && Array.isArray(worksheetData.trade_in) && worksheetData.trade_in.length > 0) {
      return worksheetData.trade_in.map((tradeIn, index) => ({
        id: index + 1,
        amount: formatPriceWithDollar(tradeIn.trade_amount ?? 0),
        acv: formatPriceWithDollar(tradeIn.acv ?? 0),
        payoff: formatPriceWithDollar(tradeIn.payoff ?? 0),
        cashBack: formatPriceWithDollar(tradeIn.cash_back ?? 0)
      }));
    } else {
      // Default to one empty trade-in if no data from API
      return [{
        id: 1,
        amount: formatPriceWithDollar(0),
        acv: formatPriceWithDollar(0),
        payoff: formatPriceWithDollar(0),
        cashBack: formatPriceWithDollar(0)
      }];
    }
  });

  // Add state for deferred downs
  const [deferredDowns, setDeferredDowns] = useState(() => {
    if (worksheetData?.def_downs && Array.isArray(worksheetData.def_downs) && worksheetData.def_downs.length > 0) {
      return worksheetData.def_downs.map((defDown, index) => ({
        id: index + 1,
        amount: formatPriceWithDollar(defDown.amount ?? 0),
        dueOn: defDown.due_on ? defDown.due_on.substring(0, 10) : ""
      }));
    } else {
      // Default to one empty deferred down if no data from API
      return [{
        id: 1,
        amount: formatPriceWithDollar(0),
        dueOn: ""
      }];
    }
  });

  const addSchedule = () => {
    if (schedules.length < 2) {
      setSchedules(prev => [...prev, {
        id: Math.max(...prev.map(s => s.id)) + 1,
        number_of_payments: "",
        desired_payment: formatPriceWithDollar(0),
        frequency: "Monthly",
        first_payment_date: "",
        final_payment_due_on: "",
        final_payment_amount: formatPriceWithDollar(0)
      }]);
    }
  };

  const removeSchedule = (id) => {
    setSchedules(prev => prev.filter(schedule => schedule.id !== id));
  };

  const updateSchedule = (id, field, value) => {
    setSchedules(prev => prev.map(schedule =>
      schedule.id === id ? { ...schedule, [field]: value } : schedule
    ));
  };

  const addTradeIn = () => {
    if (tradeIns.length < 2) {
      setTradeIns(prev => [...prev, {
        id: prev.length + 1,
        amount: formatPriceWithDollar(0),
        acv: formatPriceWithDollar(0),
        payoff: formatPriceWithDollar(0),
        cashBack: formatPriceWithDollar(0)
      }]);
    }
  };

  const removeTradeIn = (id) => {
    if (id > 1) { // Only allow removing Trade-In 2
      setTradeIns(prev => prev.filter(tradeIn => tradeIn.id !== id));
    }
  };

  const updateTradeIn = (id, field, value) => {
    setTradeIns(prev => prev.map(tradeIn =>
      tradeIn.id === id ? { ...tradeIn, [field]: value } : tradeIn
    ));
  };

  // Add functions for deferred downs
  const addDeferredDown = () => {
    setDeferredDowns(prev => [...prev, {
      id: prev.length + 1,
      amount: formatPriceWithDollar(0),
      dueOn: ""
    }]);
  };

  const removeDeferredDown = (id) => {
    if (id > 1) { // Only allow removing Deferred Down 2 and onwards
      setDeferredDowns(prev => prev.filter(defDown => defDown.id !== id));
    }
  };

  const updateDeferredDown = (id, field, value) => {
    setDeferredDowns(prev => prev.map(defDown =>
      defDown.id === id ? { ...defDown, [field]: value } : defDown
    ));
  };

  // Update tradeIns when worksheetData changes
  useEffect(() => {
    if (worksheetData?.trade_in && Array.isArray(worksheetData.trade_in) && worksheetData.trade_in.length > 0) {
      setTradeIns(worksheetData.trade_in.map((tradeIn, index) => ({
        id: index + 1,
        amount: formatPriceWithDollar(tradeIn.trade_amount),
        acv: formatPriceWithDollar(tradeIn.acv),
        payoff: formatPriceWithDollar(tradeIn.payoff),
        cashBack: formatPriceWithDollar(tradeIn.cash_back)
      })));
    }
  }, [worksheetData]);

  // Update deferred downs when worksheetData changes
  useEffect(() => {
    if (worksheetData?.def_downs && Array.isArray(worksheetData.def_downs) && worksheetData.def_downs.length > 0) {
      setDeferredDowns(worksheetData.def_downs.map((defDown, index) => ({
        id: index + 1,
        amount: formatPriceWithDollar(defDown.amount),
        dueOn: defDown.due_on ? defDown.due_on.substring(0, 10) : ""
      })));
    }
  }, [worksheetData]);

  useEffect(() => {
    setSelectedAccessories(
      parseSelectedAccessories(worksheetData?.selected_accessories).map(mapSelectedAccessoryFromApi)
    );
  }, [worksheetData?.selected_accessories]);

  const catalogAccessories = useMemo(
    () => getCatalogAccessoriesFromWorksheet(worksheetData),
    [worksheetData]
  );

  const availableCatalogAccessoryOptions = useMemo(
    () =>
      catalogAccessories.filter(
        (option) => !selectedAccessories.some((added) => isSameCatalogAccessory(option, added))
      ),
    [catalogAccessories, selectedAccessories]
  );

  const handlePickCatalogAccessory = (catalogItem) => {
    if (!canEditWorksheet || isDisabled) return;
    if (selectedAccessories.some((added) => isSameCatalogAccessory(catalogItem, added))) return;
    setSelectedAccessories((prev) => [...prev, mapCatalogPickToSelectedLine(catalogItem)]);
  };

  const handleRemoveSelectedAccessory = (index) => {
    if (!canEditWorksheet || isDisabled) return;
    setSelectedAccessories((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSelectedAccessoryChange = (index, field, value) => {
    setSelectedAccessories((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const [formValues, setFormValues] = useState({
    contract_date: worksheetData?.contract_date ? worksheetData.contract_date.substring(0, 10) : "",
    type: worksheetData?.type || "",
    sales_price: formatPriceWithDollar(worksheetData?.sales_price ?? 0),
    deposit: formatPriceWithDollar(worksheetData?.deposit ?? 0),
    trade_in: worksheetData?.trade_in,
    after_market: worksheetData?.after_market,
    accessories: worksheetData?.accessories ?? 0,
    fees: worksheetData?.fees,
    taxes: worksheetData?.taxes,
    finance_charge: worksheetData?.finance_charge ?? 0,
    amount_financed: worksheetData?.amount_financed ?? 0,
    total_down: formatPriceWithDollar(worksheetData?.total_down ?? 0),
    total_sale: worksheetData?.total_sale,
    unpaid_balance: worksheetData?.unpaid_balance,
    commissions: worksheetData?.commissions,
    lender_total_fees: worksheetData?.lender_total_fees ?? 0,
    escrow_fee: formatPriceWithDollar(worksheetData?.escrow_fee ?? 0),
    loan_acq_fee: formatPriceWithDollar(worksheetData?.loan_acq_fee ?? 0),
    flat_discount: formatPriceWithDollar(worksheetData?.flat_discount ?? 0),
    discount_percent: worksheetData?.discount_percent ?? 0,
    lender_status: worksheetData?.lender_status ?? "Working",
    washout: worksheetData?.washout,
    trade_in_acv: worksheetData?.trade_in_acv,
    sales_draw: worksheetData?.sales_draw,
    total_vehicle_cost: worksheetData?.total_vehicle_cost,
    total_discount: worksheetData?.total_discount,

    after_market_prices: worksheetData?.after_market_prices,
    after_market_costs: worksheetData?.after_market_costs,
    back_end_profit: worksheetData?.back_end_profit,
    dealer_reserves: worksheetData?.dealer_reserves,
    doc_fee: worksheetData?.doc_fee,
    total_deal_gross_profit: worksheetData?.total_deal_gross_profit,
    total_commission_paid: worksheetData?.total_commission_paid,
    total_deal_net_profit: worksheetData?.total_deal_net_profit,
    // USE RESERVE specific
    dealer_reserve: worksheetData?.dealer_reserve,
    flat_reserve: formatPriceWithDollar(worksheetData?.flat_reserve),
    buy_rate_percent: worksheetData?.buy_rate_percent,
    dealer_reserve_amount: worksheetData?.dealer_reserve_amount,
    dlr_reserve_retain_percent: worksheetData?.dlr_reserve_retain_percent ?? 0,

    // Trade-in specific fields
    trade_amount: formatPriceWithDollar(worksheetData?.trade_amount ?? 0),
    trade_acv: formatPriceWithDollar(worksheetData?.trade_acv ?? 0),
    trade_payoff: formatPriceWithDollar(worksheetData?.trade_payoff ?? 0),
    trade_cash_back: formatPriceWithDollar(worksheetData?.trade_cash_back ?? 0),
    // After market flags
    service_contract: worksheetData?.service_contract ?? false,
    gap_insurance: worksheetData?.gap_insurance ?? false,
    use_max_rate: worksheetData?.use_max_rate ?? false,
    // Initialize lender object
    lender: {
      escrow_fee: formatPriceWithDollar(worksheetData?.lender?.escrow_fee ?? 0),
      loan_acq_fee: formatPriceWithDollar(worksheetData?.lender?.loan_acq_fee ?? 0),
      flat_discount: formatPriceWithDollar(worksheetData?.lender?.flat_discount ?? 0),
      discount_percent: worksheetData?.lender?.discount_percent ?? 0,
      flat_reserve: formatPriceWithDollar(worksheetData?.lender?.flat_reserve ?? 0),
      dealer_reserve: worksheetData?.lender?.dealer_reserve ?? "No",
      buy_rate_percent: worksheetData?.lender?.buy_rate_percent ?? 0,
      dlr_reserve_retain_percent: worksheetData?.lender?.dlr_reserve_retain_percent ?? 0,
    },
  });

  useEffect(() => {


    if (formValues.type === "cash" || formValues.type === "wholesale") {
      setActiveTab("DETAIL");
    } else if (formValues.type) {
      setActiveTab("CONTRACT DETAIL");
    }
  }, [formValues.type]);

  useEffect(() => {
    if (worksheetData) {
      setFormValues({
        ...worksheetData,
        sales_price: formatPriceWithDollar(worksheetData.sales_price ?? 0),
        deposit: formatPriceWithDollar(worksheetData.deposit ?? 0),
        escrow_fee: formatPriceWithDollar(worksheetData.escrow_fee ?? 0),
        loan_acq_fee: formatPriceWithDollar(worksheetData.loan_acq_fee ?? 0),
        flat_discount: formatPriceWithDollar(worksheetData.flat_discount ?? 0),
        finder_fee: formatPriceWithDollar(worksheetData.finder_fee ?? 0),
        pack_overhead: formatPriceWithDollar(worksheetData.pack_overhead ?? 0),
        flat_commission: formatPriceWithDollar(worksheetData.flat_commission ?? 0),
        flat_reserve: formatPriceWithDollar(worksheetData.flat_reserve ?? 0),
        trade_amount: formatPriceWithDollar(worksheetData.trade_amount ?? 0),
        trade_acv: formatPriceWithDollar(worksheetData.trade_acv ?? 0),
        trade_payoff: formatPriceWithDollar(worksheetData.trade_payoff ?? 0),
        trade_cash_back: formatPriceWithDollar(worksheetData.trade_cash_back ?? 0),
        after_market: worksheetData.after_market,
        total_down: formatPriceWithDollar(worksheetData.total_down ?? 0),
        initial_cash_down: formatPriceWithDollar(worksheetData.initial_cash_down ?? 0),
        loan_rate: worksheetData?.loan_rate ?? 0,
        // Update lender object
        finance_company: {
          id: worksheetData?.finance_company?.channel_id ?? "",
          name: worksheetData?.finance_company?.name ?? "",
          escrow_fee: formatPriceWithDollar(worksheetData?.finance_company?.escrow_fee ?? 0),
          loan_acq_fee: formatPriceWithDollar(worksheetData?.finance_company?.loan_acq_fee ?? 0),
          flat_discount: formatPriceWithDollar(worksheetData?.finance_company?.flat_discount ?? 0),
          discount_percent: worksheetData?.finance_company?.discount_percent ?? 0,
          flat_reserve: formatPriceWithDollar(worksheetData?.finance_company?.flat_reserve ?? 0),
          dealer_reserve: worksheetData?.finance_company?.dealer_reserve ?? "No",
          buy_rate_percent: worksheetData?.finance_company?.buy_rate_percent ?? 0,
          dlr_reserve_retain_percent: worksheetData?.finance_company?.dlr_reserve_retain_percent ?? 0,
        },
      });

      // Update schedules if data exists
      if (worksheetData.schedules && Array.isArray(worksheetData.schedules) && worksheetData.schedules.length > 0) {
        setSchedules(worksheetData.schedules.map((schedule, index) => ({
          id: index + 1,
          number_of_payments: schedule.number_of_payments ?? "",
          desired_payment: formatPriceWithDollar(schedule.desired_payment ?? 0),
          frequency: schedule.frequency ?? "Monthly",
          first_payment_date: schedule.first_payment_date ? schedule.first_payment_date.substring(0, 10) : "",
          final_payment_due_on: schedule.final_payment_due_on ? schedule.final_payment_due_on.substring(0, 10) : "",
          final_payment_amount: formatPriceWithDollar(schedule.final_payment_amount ?? 0),
          regular_payment: schedule.regular_payment
        })));
      } else {
        // Reset to default if no schedules data
        setSchedules([{
          id: 1,
          number_of_payments: "",
          desired_payment: formatPriceWithDollar(0),
          frequency: "Monthly",
          first_payment_date: "",
          final_payment_due_on: "",
          final_payment_amount: formatPriceWithDollar(0)
        }]);
      }
    }
  }, [worksheetData]);
  const [activeLenderFeeTab, setActiveLenderFeeTab] = useState("DISCOUNT");
  const [feesPopoverOpen, setFeesPopoverOpen] = useState(false);
  const [taxesPopoverOpen, setTaxesPopoverOpen] = useState(false);
  const [feesActiveTab, setFeesActiveTab] = useState("CONTRACT");
  const [showFeeForm, setShowFeeForm] = useState(false);
  const [currentFee, setCurrentFee] = useState({ description: "", category: "Document Fee", amount: "" });

  const [showTaxForm, setShowTaxForm] = useState(false);
  const [currentTax, setCurrentTax] = useState({
    description: "",
    category: "City Sales Tax",
    amount: "",
    calc_type: "Registration State",
    tax_variable: "SalesPrice",
    tax_rate: "0.00%",
    indicator: "Fixed",
    fixed: "Yes",
    beginning_range: "0.00",
    end_range: "999,999.99",
    taxBaseAmount: "0.00"
  });
  /* --- Popover Outside Click Handler --- */
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Fees Popover Logic
      if (feesPopoverOpen) {
        const isFeesTarget = event.target.closest('#fees-popover-target');
        const isFeesPopover = event.target.closest('.fees-custom-popover');
        // Check for react-select menu (portaled) - looking for class starting with classNamePrefix
        const isSelectMenu = event.target.closest('[class*="react-select__menu"]');

        if (!isFeesTarget && !isFeesPopover && !isSelectMenu) {
          setFeesPopoverOpen(false);
        }
      }

      // Taxes Popover Logic
      if (taxesPopoverOpen) {
        const isTaxesTarget = event.target.closest('#taxes-popover-target');
        const isTaxesPopover = event.target.closest('.taxes-custom-popover');
        const isSelectMenu = event.target.closest('[class*="react-select__menu"]');

        if (!isTaxesTarget && !isTaxesPopover && !isSelectMenu) {
          setTaxesPopoverOpen(false);
        }
      }
    };

    // Use mousedown to capture the event before click/focus changes might hide elements
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [feesPopoverOpen, taxesPopoverOpen]);


  const persistWorksheetTaxAndFees = async (tax_and_fees) => {
    await updateWorksheetMutation.mutateAsync({
      id,
      data: { tax_and_fees },
      dealId,
    });
    await queryClient.invalidateQueries({ queryKey: ['worksheet', id] });
  };

  /* --- Tax & Fee Logic (worksheet JSON snapshot) --- */
  const handleSaveFee = async () => {
    try {
      const feeType = feesActiveTab === 'CONTRACT' ? 'fee-contract' : 'fee-registration';
      const feeData = {
        type: feeType,
        description: currentFee.description,
        category: currentFee.category,
        amount: toNumber(currentFee.amount),
      };

      const rows = getWorksheetTaxFeeSnapshotRows(worksheetData);
      const rowKey =
        currentFee.local_id ||
        (currentFee.id != null ? String(currentFee.id) : null) ||
        currentFee.documentId;

      if (rowKey) {
        const idx = rows.findIndex((row) => getTaxFeeRowKey(row) === rowKey);
        if (idx >= 0) {
          rows[idx] = { ...rows[idx], ...feeData, local_id: rows[idx].local_id || currentFee.local_id };
        } else {
          rows.push({ ...feeData, ...(currentFee.local_id ? { local_id: currentFee.local_id } : {}) });
        }
      } else {
        rows.push(feeData);
      }

      await persistWorksheetTaxAndFees(rows);
      setShowFeeForm(false);
      setCurrentFee({ description: "", category: "Document Fee", amount: "" });
    } catch (error) {
      console.error("Failed to save fee", error);
    }
  };

  const handleDeleteFee = async (feeItem) => {
    try {
      const rowKey = getTaxFeeRowKey(feeItem);
      if (!rowKey) return;

      const rows = getWorksheetTaxFeeSnapshotRows(worksheetData).filter(
        (row) => getTaxFeeRowKey(row) !== rowKey
      );
      await persistWorksheetTaxAndFees(rows);
    } catch (error) {
      console.error("Failed to delete fee", error);
    }
  };

  const handleSaveTax = async () => {
    try {
      const taxData = {
        type: 'tax',
        description: currentTax.description,
        category: currentTax.category,
        amount: toNumber(currentTax.amount),
        calc_type: currentTax.calc_type,
        tax_variable: currentTax.tax_variable,
        tax_rate: toNumber(currentTax.tax_rate),
        indicator: currentTax.indicator,
        fixed: currentTax.fixed === 'Yes',
        beginning_range: toNumber(currentTax.beginning_range),
        end_range: toNumber(currentTax.end_range),
      };

      const rows = getWorksheetTaxFeeSnapshotRows(worksheetData);
      const rowKey =
        currentTax.local_id ||
        (currentTax.id != null ? String(currentTax.id) : null) ||
        currentTax.documentId;

      if (rowKey) {
        const idx = rows.findIndex((row) => getTaxFeeRowKey(row) === rowKey);
        if (idx >= 0) {
          rows[idx] = { ...rows[idx], ...taxData, local_id: rows[idx].local_id || currentTax.local_id };
        } else {
          rows.push({ ...taxData, ...(currentTax.local_id ? { local_id: currentTax.local_id } : {}) });
        }
      } else {
        rows.push(taxData);
      }

      await persistWorksheetTaxAndFees(rows);
      setShowTaxForm(false);
    } catch (error) {
      console.error("Failed to save tax", error);
    }
  };

  const handleDeleteTax = async (taxItem) => {
    try {
      const rowKey = getTaxFeeRowKey(taxItem);
      if (!rowKey) return;

      const rows = getWorksheetTaxFeeSnapshotRows(worksheetData).filter(
        (row) => getTaxFeeRowKey(row) !== rowKey
      );
      await persistWorksheetTaxAndFees(rows);
    } catch (error) {
      console.error("Failed to delete tax", error);
    }
  };

  const handleResetTaxAndFees = async () => {
    try {
      await updateWorksheetMutation.mutateAsync({
        id,
        data: { reset_tax_and_fees: true },
        dealId,
      });
      await queryClient.invalidateQueries({ queryKey: ['worksheet', id] });
      setShowFeeForm(false);
      setShowTaxForm(false);
    } catch (error) {
      console.error("Failed to reset fees/taxes", error);
    }
  };
  useEffect(() => {
    setFormValues((prev) => ({
      ...prev,
      type: type || initialData?.type || prev.type,
    }));
  }, [type, initialData?.type]);

  useImperativeHandle(ref, () => ({
    getValues: () => ({
      ...formValues,
      trade_in: tradeIns.map(tradeIn => ({
        trade_amount: parseFloat(tradeIn.amount.toString().replace(/[^\d.]/g, '')) || 0,
        acv: parseFloat(tradeIn.acv.toString().replace(/[^\d.]/g, '')) || 0,
        payoff: parseFloat(tradeIn.payoff.toString().replace(/[^\d.]/g, '')) || 0,
        cash_back: parseFloat(tradeIn.cashBack.toString().replace(/[^\d.]/g, '')) || 0
      })),
      def_downs: deferredDowns.map(defDown => ({
        amount: parseFloat(defDown.amount.toString().replace(/[^\d.]/g, '')) || 0,
        due_on: defDown.dueOn
      })),
      schedules: schedules.map(schedule => ({
        number_of_payments: parseInt(schedule.number_of_payments) || 0,
        desired_payment: parseFloat(schedule.desired_payment.toString().replace(/[^\d.]/g, '')) || 0,
        frequency: schedule.frequency,
        first_payment_date: schedule.first_payment_date,
        final_payment_due_on: schedule.final_payment_due_on,
        final_payment_amount: parseFloat(schedule.final_payment_amount.toString().replace(/[^\d.]/g, '')) || 0
      })),
      def_downs: deferredDowns.map(defDown => ({
        amount: parseFloat(defDown.amount.toString().replace(/[^\d.]/g, '')) || 0,
        due_on: defDown.dueOn
      }))
    }),
  }));

  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [addedVehicles, setAddedVehicles] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  console.log("addedVehicles",addedVehicles)
  const handleAddVehicleClick = () => {
    setShowAddVehicle(true);
  };

  const handleCloseAddVehicle = () => {
    setShowAddVehicle(false);
  };

  const handleVehicleAdded = (vehicles) => {
    setAddedVehicles(vehicles);
    if (vehicles && vehicles.length > 0) {
      const price = toNumber(vehicles[0].price);
      let loanRate = 0;

      if (aprData?.results?.[0]?.apr) {
        const aprRanges = aprData.results[0].apr;
        const matchingRange = aprRanges.find(range => {
          const min = parseFloat(range.min);
          const max = range.max === null ? Infinity : parseFloat(range.max);
          return price >= min && price < max;
        });

        if (matchingRange) {
          loanRate = parseFloat(matchingRange.apr);
        }
      }

      setFormValues((prev) => ({
        ...prev,
        sales_price: formatPriceWithDollar(price),
        loan_rate: loanRate || prev.loan_rate,
      }));
    }
    // You can also update the worksheet data here if needed
  };

  useEffect(() => {
    if (worksheetData?.inventory) {
      setAddedVehicles([
        {
          make: worksheetData.inventory.inventory_vehicle.make,
          model: worksheetData.inventory.inventory_vehicle.model,
          vin: worksheetData.inventory.vin,
          stockNumber: worksheetData.inventory.stock_number,
          vehicle_image: worksheetData.inventory.vehicle_image,
          status: worksheetData.inventory?.inventory_vehicle?.vehicle_status?.status_name,
          status_color: worksheetData.inventory?.inventory_vehicle?.vehicle_status?.color,
          inventory_status_name: worksheetData.inventory?.inventory_status_name,
          mileage: worksheetData.inventory?.inventory_vehicle?.actual_mileage,
          documentId: worksheetData.inventory?.documentId,
          id: worksheetData.inventory?.id,
        }
      ]);
    }
  }, [worksheetData]);

  const nextDayFromContract = () => {
    if (!formValues.contract_date) return "";
    const d = new Date(formValues.contract_date);
    d.setDate(d.getDate() + 1);
    return d.toLocaleDateString("en-US");
  };


  const handleSaveWorksheet = async () => {
    try {
      const values = {
        ...(addedVehicles && addedVehicles.length > 0 && addedVehicles[0].documentId && { inventory: addedVehicles[0].documentId }),
        sales_price: toNumber(formValues.sales_price),
        deposit: toNumber(formValues.deposit),
        contract_date: formatDateForAPI(formValues.contract_date),
        // After market selections
        service_contract: !!formValues.service_contract,
        gap_insurance: !!formValues.gap_insurance,
        service_contract_fields: formValues.service_contract ? {
          price: toNumber(formValues.sc_price),
          cost: toNumber(formValues.sc_cost),
          code: formValues.sc_code || '',
          company: formValues.sc_company || '',
          mileage: formValues.sc_mileage || '',
          term: formValues.sc_term || '',
          description: formValues.sc_description || ''
        } : undefined,
        gap_insurance_fields: formValues.gap_insurance ? {
          price: toNumber(formValues.gap_price),
          cost: toNumber(formValues.gap_cost),
          code: formValues.gap_code || '',
          company: formValues.gap_company || '',
          mileage: formValues.gap_mileage || '',
          term: formValues.gap_term || '',
          description: formValues.gap_description || ''
        } : undefined,
        trade_in: tradeIns.map(tradeIn => ({
          trade_amount: toNumber(tradeIn.amount),
          acv: toNumber(tradeIn.acv),
          payoff: toNumber(tradeIn.payoff),
          cash_back: toNumber(tradeIn.cashBack)
        })),
        def_downs: deferredDowns.map(defDown => ({
          amount: toNumber(defDown.amount),
          due_on: defDown.dueOn
        })),
        schedules: schedules.map(schedule => ({
          number_of_payments: parseInt(schedule.number_of_payments) || 0,
          desired_payment: parseFloat(schedule.desired_payment.toString().replace(/[^\d.]/g, '')) || 0,
          frequency: schedule.frequency,
          first_payment_date: schedule.first_payment_date,
          final_payment_due_on: schedule.final_payment_due_on,
          final_payment_amount: parseFloat(schedule.final_payment_amount.toString().replace(/[^\d.]/g, '')) || 0
        })),
        commissions: {
          finder_fee: toNumber(formValues?.commissions?.finder_fee),
          sales_gross_percent: toNumber(formValues?.commissions?.sales_gross_percent),
          comm_gross_percent: toNumber(formValues?.commissions?.comm_gross_percent),
          front_end_percent: toNumber(formValues?.commissions?.front_end_percent),
          back_end_percent: toNumber(formValues?.commissions?.back_end_percent),
          down_pay_percent: toNumber(formValues?.commissions?.down_pay_percent),
          def_down_pay_comm_percent: toNumber(formValues?.commissions?.def_down_pay_comm_percent),
          dealer_reserve_comm_percent: toNumber(formValues?.commissions?.dealer_reserve_comm_percent),
          flat_commission: toNumber(formValues?.commissions?.flat_commission),
          flat_reserve: toNumber(formValues?.commissions?.flat_reserve),
          buy_rate_percent: toNumber(formValues?.commissions?.buy_rate_percent),
          dealer_reserve_amount: toNumber(formValues?.commissions?.dealer_reserve_amount),
          dlr_reserve_retain_percent: toNumber(formValues?.commissions?.dlr_reserve_retain_percent),
          pack_overhead: toNumber(formValues?.commissions?.pack_overhead),
        },
        finance_company: {
          escrow_fee: toNumber(formValues?.finance_company?.escrow_fee),
          loan_acq_fee: toNumber(formValues?.finance_company?.loan_acq_fee),
          flat_discount: toNumber(formValues?.finance_company?.flat_discount),
          discount_percent: toNumber(formValues?.finance_company?.discount_percent),
          flat_reserve: toNumber(formValues?.finance_company?.flat_reserve),
          dealer_reserve: formValues?.finance_company?.dealer_reserve,
          buy_rate_percent: toNumber(formValues?.finance_company?.buy_rate_percent),
          dlr_reserve_retain_percent: toNumber(formValues?.finance_company?.dlr_reserve_retain_percent),
        },
        after_market: formValues.after_market,
        selected_accessories: mapSelectedAccessoriesForApi(selectedAccessories),
        loan_rate: toNumber(formValues?.loan_rate),
        // Send total_down as initial_cash_down for non-cash types
        ...(formValues.type !== "cash" && { initial_cash_down: toNumber(formValues.initial_cash_down) }),
      };

      updateWorksheetMutation.mutate({
        id: id,
        data: values,
        dealId: dealId,
      });
    } catch (error) {
      console.error("Error saving worksheet:", error);
    }
  };

  return (
    <div
      className={classNames("worksheet-form", {
        "worksheet-form--theme2": isTheme2,
        "worksheet-form--add-vehicle": showAddVehicle,
      })}
    >
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      {showAddVehicle ? (
        <AddVehicle
          activeColor={activeColor}
          isLightTheme={isLightTheme}
          isTheme2={isTheme2}
          onClose={handleCloseAddVehicle}
          onVehicleAdded={handleVehicleAdded}
        />
      ) :
        <Form className={classNames({ "worksheet-form-fields": isTheme2 })}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <CardTitle tag="h4" style={{ margin: 0 }}>Worksheets</CardTitle>
            <div style={{ display: 'flex', alignItems: 'center' }}>

              <Button
                color="secondary"
                size="sm"
                className="mr-2"
                onClick={handleCancelWorksheet}
                disabled={updateWorksheetMutation.isPending}
              >
                Cancel
              </Button>
              {canEditWorksheet && (
                <Button
                  size="sm"
                  style={{ ...activeColorStyle, color: 'white' }}
                  onClick={handleSaveWorksheet}
                  disabled={isDisabled || updateWorksheetMutation.isPending}
                >
                  {updateWorksheetMutation.isPending ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
              )}
            </div>

          </div>

          <Row>
            <Col md="6">
              <Nav tabs className="border-0" style={{ borderBottom: "none" }}>
                {["CONTRACT DETAIL", "DEF DOWNS", "DETAIL", "TRADE-IN", "AFTER MARKET", "ACCESSORIES"]
                  .filter(tab => {
                    if (formValues.type === "wholesale") return tab === "DETAIL";
                    if (tab === "CONTRACT DETAIL") return formValues.type !== "cash";
                    if (tab === "DEF DOWNS") return formValues.type !== "cash";
                    if (tab === "DETAIL") return formValues.type === "cash";
                    return true;
                  })
                  .map(tab => (
                    <NavItem key={tab}>
                      <NavLink
                        href="#"
                        className={activeTab === tab ? "active font-weight-bold" : "text-muted"}
                        style={{
                          background: "none",
                          border: "none",
                          // fontSize: 16,
                          // marginRight: 8,
                          position: "relative",
                          // fontWeight: 500,
                          cursor: 'pointer',
                          color: activeTab === tab ? activeColorStyle.borderColor : undefined,
                          fontWeight: activeTab === tab ? 700 : 500

                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab(tab);
                        }}
                      >
                        {tab.toUpperCase()}
                        {activeTab === tab && (
                          <div style={{ height: 3, background: activeColorStyle.borderColor, borderRadius: 2, position: "absolute", left: 0, right: 0, bottom: -8 }} />
                        )}
                      </NavLink>
                    </NavItem>
                  ))}
              </Nav>
              <TabContent className="mb-0" activeTab={activeTab}>
                <TabPane tabId="CONTRACT DETAIL">
                  <Card className="mb-3" style={{ ...getCardShadowStyle(), padding: 10 }}>
                    <CardTitle tag="h4" style={{ marginTop: 5, marginBottom: 5, fontWeight: 600, fontSize: 14 }}>LENDER: <a href={`/channels/${formValues?.finance_company?.id}`} style={{ textDecoration: 'underline', color: isLightTheme ? 'black' : 'white' }} target="_blank" rel="noopener noreferrer" > {formValues?.finance_company?.name}</a></CardTitle>
                    <Row>
                      <Col md="3">
                        <FormGroup>
                          <Label for="contract_date" style={{ color: getTextColor(), fontSize: 12 }}>Contract Date</Label>
                          <ReactDatetime
                            inputProps={getDateInputProps({
                              placeholder: "Select Date",
                              disabled: isDisabled,
                            })}
                            dateFormat="MM-DD-YYYY"
                            timeFormat={false}
                            closeOnSelect={true}
                            value={formValues.contract_date ? moment(formValues.contract_date, "YYYY-MM-DD") : null}
                            onChange={(date) =>
                              handleChange({
                                target: {
                                  name: "contract_date",
                                  value: date && moment(date).isValid() ? moment(date).format("YYYY-MM-DD") : ""
                                }
                              })
                            }
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label for="sales_price" style={{ color: getTextColor() }}>Sales Price</Label>
                          <Input
                            className="form-control"
                            style={inputStyle14}
                            type="text"
                            name="sales_price"
                            id="sales_price"
                            value={formValues.sales_price}
                            onChange={(e) => handleChange(e)}
                            disabled={isDisabled}
                            onBlur={(e) => {
                              setFormValues(prev => ({
                                ...prev,
                                sales_price: formatPriceWithDollar(formValues.sales_price)
                              }));
                            }}
                            onFocus={(e) => {
                              const raw = formValues.sales_price.toString().replace(/[^\d.]/g, '');
                              setFormValues(prev => ({ ...prev, sales_price: raw }));
                            }}
                            placeholder="0.00"
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label for="initial_cash_down" style={{ color: getTextColor() }}>Initial Cash Down</Label>
                          <Input
                            className="form-control"
                            style={inputStyle14}
                            type="text"
                            name="initial_cash_down"
                            id="initial_cash_down"
                            value={formValues.initial_cash_down}
                            onChange={(e) => handleChange(e)}
                            disabled={isDisabled}
                            onBlur={(e) => {
                              setFormValues(prev => ({
                                ...prev,
                                initial_cash_down: formatPriceWithDollar(formValues.initial_cash_down)
                              }));
                            }}
                            onFocus={(e) => {
                              const raw = formValues.initial_cash_down.toString().replace(/[^\d.]/g, '');
                              setFormValues(prev => ({ ...prev, initial_cash_down: raw }));
                            }}
                            placeholder="0.00"
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label for="loan_rate" style={{ color: getTextColor() }}>Loan Rate</Label>
                          <Input
                            className="form-control"
                            style={inputStyle14}
                            type="text"
                            name="loan_rate"
                            id="loan_rate"
                            value={formValues.loan_rate ? `${formValues.loan_rate}%` : ''}
                            disabled={isDisabled}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^\d.]/g, '');

                              // Ensure only one decimal point
                              const parts = value.split('.');
                              if (parts.length > 2) {
                                value = parts[0] + '.' + parts.slice(1).join('');
                              }

                              // Convert to number to check range
                              const numValue = parseFloat(value);
                              if (!isNaN(numValue)) {
                                if (numValue > 100) {
                                  value = '100';
                                } else if (numValue < 0) {
                                  value = '0';
                                }
                              }

                              setFormValues(prev => ({ ...prev, loan_rate: value }));
                            }}
                            placeholder="0.00%"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="12">
                        <FormGroup check>
                          <Label check style={{ color: getTextColor() }}>
                            <Input
                              type="checkbox"
                              name="use_max_rate"
                              disabled={isDisabled}
                              checked={formValues.use_max_rate}
                              onChange={(e) => {
                                setFormValues(prev => ({
                                  ...prev,
                                  use_max_rate: e.target.checked
                                }));
                              }}
                            />
                            <span className="form-check-sign"></span>
                            Use Max Rate
                          </Label>
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Schedule Sections inside Contract Detail */}
                    <div className="mt-4">
                      {schedules.map((schedule, index) => (
                        <div key={schedule.id} className="mb-3" >
                          <CardHeader role="tab" style={{ padding: "8px 12px", cursor: "pointer", backgroundColor: currentTheme ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}>
                            <div style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center"
                            }}>
                              <span style={{ fontSize: "13px", color: getTextColor(), fontWeight: "500" }}>Schedule {schedule.id}</span>
                              <span className="ml-auto" style={{ fontWeight: "700", fontSize: "14px", backgroundColor: "#e1effe", color: "#1f8ef1", padding: "2px 8px", borderRadius: "4px", marginRight: schedule.id > 1 ? "10px" : "0" }}>
                                {formatPriceWithDollar(schedule.regular_payment)}
                              </span>
                              {schedule.id > 1 && (
                                <Button
                                  size="sm"
                                  color="danger"
                                  onClick={() => removeSchedule(schedule.id)}
                                  style={{ padding: '2px 6px', fontSize: '11px' }}
                                  disabled={isDisabled}
                                >
                                  ×
                                </Button>
                              )}
                            </div>
                          </CardHeader>
                          <CardBody style={{ padding: "0rem 0rem 0.5rem 0rem " }}>
                            <Row>
                              <Col md="3">
                                <FormGroup>
                                  <Label style={{ color: getTextColor(), fontSize: "12px" }}># of Payments</Label>
                                  <Input
                                    className="form-control"
                                    type="number"
                                    value={schedule.number_of_payments}
                                    onChange={(e) => updateSchedule(schedule.id, 'number_of_payments', e.target.value)}
                                    style={inputStyle13}
                                    placeholder="0"
                                    disabled={isDisabled}
                                  />
                                </FormGroup>
                              </Col>
                              <Col md="3">
                                <FormGroup>
                                  <Label style={{ color: getTextColor(), fontSize: "12px" }}>Desired Payment</Label>
                                  <Input
                                    className="form-control"
                                    type="text"
                                    value={schedule.desired_payment}
                                    onChange={(e) => updateSchedule(schedule.id, 'desired_payment', e.target.value)}
                                    onBlur={(e) => {
                                      updateSchedule(schedule.id, 'desired_payment', formatPriceWithDollar(schedule.desired_payment));
                                    }}
                                    onFocus={(e) => {
                                      const raw = schedule.desired_payment.toString().replace(/[^\d.]/g, '');
                                      updateSchedule(schedule.id, 'desired_payment', raw);
                                    }}
                                    style={inputStyle13}
                                    placeholder="0.00"
                                    disabled={isDisabled}
                                  />
                                </FormGroup>
                              </Col>
                              <Col md="3">
                                <FormGroup>
                                  <Label style={{ color: getTextColor(), fontSize: "12px" }}>Frequency</Label>
                                  <Select
                                    styles={resolvedSelectStyles}
                                    className="react-select"
                                    placeholder="Select Frequency"
                                    name="frequency"
                                    isSearchable={false}
                                    closeMenuOnSelect={true}
                                    isDisabled={isDisabled}
                                    value={schedule.frequency ? {
                                      value: schedule.frequency,
                                      label: schedule.frequency
                                    } : null}
                                    onChange={(option) => updateSchedule(schedule.id, 'frequency', option?.value || '')}
                                    options={[
                                      { value: "Weekly", label: "Weekly" },
                                      { value: "Bi-Weekly", label: "Bi-Weekly" },
                                      { value: "Semi-monthly", label: "Semi-monthly" },
                                      { value: "Monthly", label: "Monthly" }
                                    ]}
                                  />
                                </FormGroup>
                              </Col>
                              <Col md="3">
                                <FormGroup>
                                  <Label style={{ color: getTextColor(), fontSize: "12px" }}>First Payment Date</Label>
                                  <ReactDatetime
                                    inputProps={getDateInputProps13({
                                      placeholder: "Select Date",
                                      disabled: isDisabled,
                                    })}
                                    dateFormat="MM-DD-YYYY"
                                    timeFormat={false}
                                    closeOnSelect={true}
                                    value={schedule.first_payment_date ? moment(schedule.first_payment_date, "YYYY-MM-DD") : null}
                                    onChange={(date) =>
                                      updateSchedule(
                                        schedule.id,
                                        'first_payment_date',
                                        date && moment(date).isValid() ? moment(date).format("YYYY-MM-DD") : ""
                                      )
                                    }
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row>
                              <Col md="6" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FormGroup>
                                  <Label style={{ color: getTextColor(), fontSize: "15px" }}>Final Payment Due On <span style={{ fontWeight: 600 }}>{formatDateForDisplay(schedule.final_payment_due_on)}</span></Label>
                                </FormGroup>
                              </Col>
                              <Col md="6" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FormGroup>
                                  <Label style={{ color: getTextColor(), fontSize: "15px" }}>Final Payment Amount <span style={{ fontWeight: 600 }}>{schedule.final_payment_amount}</span></Label>

                                </FormGroup>
                              </Col>
                            </Row>
                          </CardBody>
                          {schedules.length < 2 && (
                            <Row>
                              <Col md="12">
                                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                  <Button
                                    size="sm"
                                    style={{ ...activeColorStyle, color: 'white' }}
                                    onClick={addSchedule}
                                    disabled={isDisabled}
                                  >
                                    Add Schedule
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          )}
                        </div>
                      ))}
                    </div>
                  </Card>
                </TabPane>

                <TabPane tabId="DETAIL">
                  <Card className="mb-2" style={{ ...getCardShadowStyle(), padding: 10 }}>
                    <Row>
                      <Col md="4">
                        <FormGroup>
                          <Label for="contract_date" style={{ color: getTextColor() }}>Contract Date</Label>
                          <ReactDatetime
                            inputProps={getDateInputProps({
                              placeholder: "Select Date",
                              disabled: isDisabled,
                            })}
                            dateFormat="MM-DD-YYYY"
                            timeFormat={false}
                            closeOnSelect={true}
                            value={formValues.contract_date ? moment(formValues.contract_date, "YYYY-MM-DD") : null}
                            onChange={(date) =>
                              handleChange({
                                target: {
                                  name: "contract_date",
                                  value: date && moment(date).isValid() ? moment(date).format("YYYY-MM-DD") : ""
                                }
                              })
                            }
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label for="sales_price" style={{ color: getTextColor() }}>Sales Price</Label>
                          <Input
                            className="form-control"
                            style={inputStyle14}
                            type="text"
                            name="sales_price"
                            id="sales_price"
                            value={formValues.sales_price}
                            onChange={(e) => handleChange(e)}
                            disabled={isDisabled}
                            onBlur={(e) => {
                              setFormValues(prev => ({
                                ...prev,
                                sales_price: formatPriceWithDollar(formValues.sales_price)
                              }));
                            }}
                            onFocus={(e) => {
                              const raw = formValues.sales_price.toString().replace(/[^\d.]/g, '');
                              setFormValues(prev => ({ ...prev, sales_price: raw }));
                            }}
                            placeholder="0.00"
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label for="deposit" style={{ color: getTextColor() }}>Deposit</Label>
                          <Input
                            className="form-control"
                            style={inputStyle14}
                            type="text"
                            name="deposit"
                            id="deposit"
                            value={formValues.deposit}
                            onChange={(e) => handleChange(e)}
                            disabled={isDisabled}
                            onBlur={(e) => {
                              setFormValues(prev => ({
                                ...prev,
                                deposit: formatPriceWithDollar(formValues.deposit)
                              }));
                            }}
                            onFocus={(e) => {
                              const raw = formValues.deposit.toString().replace(/[^\d.]/g, '');
                              setFormValues(prev => ({ ...prev, deposit: raw }));
                            }}
                            placeholder="0.00"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </Card>
                </TabPane>

                <TabPane tabId="DEF DOWNS">
                  <Card className="mb-3" style={{ ...getCardShadowStyle(), padding: 10 }}>
                    {deferredDowns.map((defDown, index) => (
                      <div key={defDown.id}>
                        <Row>
                          <Col md="12">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <h5 style={{ color: getTextColor(), fontWeight: 600, margin: 0 }}>Deferred Down {defDown.id}</h5>
                              {defDown.id > 1 && (
                                <Button
                                  size="sm"
                                  color="danger"
                                  onClick={() => removeDeferredDown(defDown.id)}
                                  style={{ padding: '2px 8px', fontSize: '12px' }}
                                  disabled={isDisabled}
                                >
                                  ×
                                </Button>
                              )}
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col md="6">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Deferred Down:</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="text"
                                value={defDown.amount}
                                disabled={isDisabled}
                                onChange={(e) => updateDeferredDown(defDown.id, 'amount', e.target.value)}
                                onBlur={(e) => {
                                  updateDeferredDown(defDown.id, 'amount', formatPriceWithDollar(defDown.amount));
                                }}
                                onFocus={(e) => {
                                  const raw = defDown.amount.toString().replace(/[^\d.]/g, '');
                                  updateDeferredDown(defDown.id, 'amount', raw);
                                }}
                                placeholder="0.00"
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Due On:</Label>
                              <ReactDatetime
                                inputProps={getDateInputProps({
                                  placeholder: "Select Date",
                                  disabled: isDisabled,
                                })}
                                dateFormat="MM-DD-YYYY"
                                timeFormat={false}
                                closeOnSelect={true}
                                value={defDown.dueOn ? moment(defDown.dueOn, "YYYY-MM-DD") : null}
                                onChange={(date) =>
                                  updateDeferredDown(
                                    defDown.id,
                                    'dueOn',
                                    date && moment(date).isValid() ? moment(date).format("YYYY-MM-DD") : ""
                                  )
                                }
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </div>
                    ))}
                    <Row>
                      <Col md="12">
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                          <Button
                            size="sm"
                            style={{ ...activeColorStyle, color: 'white' }}
                            onClick={addDeferredDown}
                            disabled={isDisabled}
                          >
                            Add Deferred Down
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </TabPane>

                <TabPane tabId="TRADE-IN">
                  <Card className="mb-3" style={{ ...getCardShadowStyle(), padding: 10 }}>
                    {tradeIns.map((tradeIn, index) => (
                      <div key={tradeIn.id} >
                        <Row>
                          <Col md="12">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <h5 style={{ color: getTextColor(), fontWeight: 600, margin: 0 }}>Trade-In {tradeIn.id} {tradeIn.amount}</h5>
                              {tradeIn.id > 1 && (
                                <Button
                                  size="sm"
                                  color="danger"
                                  onClick={() => removeTradeIn(tradeIn.id)}
                                  style={{ padding: '2px 8px', fontSize: '12px' }}
                                  disabled={isDisabled}
                                >
                                  ×
                                </Button>
                              )}
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col md="3">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Trade Amount:</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="text"
                                value={tradeIn.amount}
                                disabled={isDisabled}
                                onChange={(e) => updateTradeIn(tradeIn.id, 'amount', e.target.value)}
                                onBlur={(e) => {
                                  updateTradeIn(tradeIn.id, 'amount', formatPriceWithDollar(tradeIn.amount));
                                }}
                                onFocus={(e) => {
                                  const raw = tradeIn.amount.toString().replace(/[^\d.]/g, '');
                                  updateTradeIn(tradeIn.id, 'amount', raw);
                                }}
                                placeholder="0.00"
                              />
                            </FormGroup>
                          </Col>
                          <Col md="3">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>ACV:</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="text"
                                value={tradeIn.acv}
                                disabled={isDisabled}
                                onChange={(e) => updateTradeIn(tradeIn.id, 'acv', e.target.value)}
                                onBlur={(e) => {
                                  updateTradeIn(tradeIn.id, 'acv', formatPriceWithDollar(tradeIn.acv));
                                }}
                                onFocus={(e) => {
                                  const raw = tradeIn.acv.toString().replace(/[^\d.]/g, '');
                                  updateTradeIn(tradeIn.id, 'acv', raw);
                                }}
                                placeholder="0.00"
                              />
                            </FormGroup>
                          </Col>
                          <Col md="3">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Payoff:</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="text"
                                value={tradeIn.payoff}
                                disabled={isDisabled}
                                onChange={(e) => updateTradeIn(tradeIn.id, 'payoff', e.target.value)}
                                onBlur={(e) => {
                                  updateTradeIn(tradeIn.id, 'payoff', formatPriceWithDollar(tradeIn.payoff));
                                }}
                                onFocus={(e) => {
                                  const raw = tradeIn.payoff.toString().replace(/[^\d.]/g, '');
                                  updateTradeIn(tradeIn.id, 'payoff', raw);
                                }}
                                placeholder="0.00"
                              />
                            </FormGroup>
                          </Col>
                          <Col md="3">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Cash Back:</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="text"
                                value={tradeIn.cashBack}
                                disabled={isDisabled}
                                onChange={(e) => updateTradeIn(tradeIn.id, 'cashBack', e.target.value)}
                                onBlur={(e) => {
                                  updateTradeIn(tradeIn.id, 'cashBack', formatPriceWithDollar(tradeIn.cashBack));
                                }}
                                onFocus={(e) => {
                                  const raw = tradeIn.cashBack.toString().replace(/[^\d.]/g, '');
                                  updateTradeIn(tradeIn.id, 'cashBack', raw);
                                }}
                                placeholder="0.00"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </div>
                    ))}
                    <Row>
                      <Col md="12">
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>

                          <Button
                            size="sm"
                            style={{ ...activeColorStyle, color: 'white' }}
                            onClick={addTradeIn}
                            disabled={isDisabled || tradeIns.length >= 2}
                          >
                            Add a Trade-In
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </TabPane>

                <TabPane tabId="AFTER MARKET">
                  <Card className="mb-3" style={{ ...getCardShadowStyle(), padding: 10 }}>
                    <Row>
                      <Col md="6">
                        <FormGroup check>
                          <Label check style={{ color: getTextColor() }}>
                            <Input
                              type="checkbox"
                              checked={!!formValues.after_market?.service_contract}
                              onChange={(e) => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, service_contract: e.target.checked } }))}
                            />
                            <span className="form-check-sign" /> Service Contract
                          </Label>

                        </FormGroup>
                      </Col>
                      <Col md="6">

                        <FormGroup check>
                          <Label check style={{ color: getTextColor() }}>
                            <Input
                              type="checkbox"
                              checked={!!formValues.after_market?.gap_insurance}
                              onChange={(e) => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, gap_insurance: e.target.checked } }))}
                            />
                            <span className="form-check-sign" /> GAP Insurance
                          </Label>
                        </FormGroup>
                      </Col>
                    </Row>

                    {formValues.after_market?.service_contract && (
                      <>
                        <Row className="mt-3">
                          <Col md="12">
                            <h5 style={{ color: getTextColor(), fontWeight: 600, margin: 0 }}>Service Contract</h5>
                          </Col>
                        </Row>
                        <Row className="mt-3">

                          <Col md="6">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Price</Label>

                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="text"
                                value={formValues.after_market?.sc_price || ''}
                                disabled={isDisabled}
                                onChange={(e) => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, sc_price: e.target.value } }))}
                                onBlur={() => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, sc_price: formatPriceWithDollar(prev.after_market?.sc_price || 0) } }))}
                                onFocus={() => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, sc_price: (prev.after_market?.sc_price || '').toString().replace(/[^\d.]/g, '') } }))}
                                placeholder="0.00"
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Cost</Label>

                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="text"
                                value={formValues.after_market?.sc_cost || ''}
                                disabled={isDisabled}
                                onChange={(e) => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, sc_cost: e.target.value } }))}
                                onBlur={() => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, sc_cost: formatPriceWithDollar(prev.after_market?.sc_cost || 0) } }))}
                                onFocus={() => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, sc_cost: (prev.after_market?.sc_cost || '').toString().replace(/[^\d.]/g, '') } }))}
                                placeholder="0.00"
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Code</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="text"
                                value={formValues.after_market?.sc_code || ''}
                                disabled={isDisabled}
                                onChange={(e) => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, sc_code: e.target.value } }))}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Company</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="text"
                                value={formValues.after_market?.sc_company || ''}
                                disabled={isDisabled}
                                onChange={(e) => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, sc_company: e.target.value } }))}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Mileage</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="text"
                                value={formValues.after_market?.sc_mileage || ''}
                                disabled={isDisabled}
                                onChange={(e) => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, sc_mileage: e.target.value } }))}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Term</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="text"
                                value={formValues.after_market?.sc_term || ''}
                                disabled={isDisabled}
                                onChange={(e) => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, sc_term: e.target.value } }))}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="12">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Description</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="textarea"
                                value={formValues.after_market?.sc_description || ''}
                                disabled={isDisabled}
                                onChange={(e) => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, sc_description: e.target.value } }))}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </>
                    )}

                    {formValues.after_market?.gap_insurance && (
                      <>
                        <Row className="mt-3">
                          <Col md="12">
                            <h5 style={{ color: getTextColor(), fontWeight: 600, margin: 0 }}>GAP Insurance</h5>
                          </Col>
                        </Row>
                        <Row className="mt-3">
                          <Col md="6">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Price</Label>

                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="text"
                                value={formValues.after_market?.gap_price || ''}
                                disabled={isDisabled}
                                onChange={(e) => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, gap_price: e.target.value } }))}
                                onBlur={() => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, gap_price: formatPriceWithDollar(prev.after_market?.gap_price || 0) } }))}
                                onFocus={() => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, gap_price: (prev.after_market?.gap_price || '').toString().replace(/[^\d.]/g, '') } }))}
                                placeholder="0.00"
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Cost</Label>

                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="text"
                                value={formValues.after_market?.gap_cost || ''}
                                disabled={isDisabled}
                                onChange={(e) => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, gap_cost: e.target.value } }))}
                                onBlur={() => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, gap_cost: formatPriceWithDollar(prev.after_market?.gap_cost || 0) } }))}
                                onFocus={() => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, gap_cost: (prev.after_market?.gap_cost || '').toString().replace(/[^\d.]/g, '') } }))}
                                placeholder="0.00"
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Code</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="text"
                                value={formValues.after_market?.gap_code || ''}
                                disabled={isDisabled}
                                onChange={(e) => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, gap_code: e.target.value } }))}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Company</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="text"
                                value={formValues.after_market?.gap_company || ''}
                                disabled={isDisabled}
                                onChange={(e) => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, gap_company: e.target.value } }))}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Mileage</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="text"
                                value={formValues.after_market?.gap_mileage || ''}
                                disabled={isDisabled}
                                onChange={(e) => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, gap_mileage: e.target.value } }))}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Term</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="text"
                                value={formValues.after_market?.gap_term || ''}
                                disabled={isDisabled}
                                onChange={(e) => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, gap_term: e.target.value } }))}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="12">
                            <FormGroup>
                              <Label style={{ color: getTextColor() }}>Description</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                type="textarea"
                                value={formValues.after_market?.gap_description || ''}
                                disabled={isDisabled}
                                onChange={(e) => setFormValues(prev => ({ ...prev, after_market: { ...prev.after_market, gap_description: e.target.value } }))}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </>
                    )}
                  </Card>
                </TabPane>

                <TabPane tabId="ACCESSORIES">
                  <Card className="mb-3" style={{ ...getCardShadowStyle(), padding: "6px 8px" }}>
                    {catalogAccessories.length > 0 && canEditWorksheet && !isDisabled && (
                      <UncontrolledDropdown className="mb-2">
                        <DropdownToggle
                          caret
                          size="sm"
                          tag={Button}
                          style={{ ...activeColorStyle, color: "white" }}
                        >
                          <i className="tim-icons icon-simple-add mr-1" />
                          Add Accessory
                        </DropdownToggle>
                        <DropdownMenu>
                          {availableCatalogAccessoryOptions.map((option) => (
                            <DropdownItem
                              key={option.documentId || option.id}
                              onClick={() => handlePickCatalogAccessory(option)}
                            >
                              {option.display_name || option.name || ""}
                            </DropdownItem>
                          ))}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    )}

                    {selectedAccessories.map((item, idx) => (
                      <Card
                        key={`${getCatalogAccessoryKey(item) ?? "row"}-${idx}`}
                        className="mb-2"
                        style={{
                          backgroundColor: isLightTheme ? "#f4f5f7" : "rgba(255, 255, 255, 0.06)",
                          border: `1px solid ${isLightTheme ? "#e3e6ef" : "rgba(255, 255, 255, 0.12)"}`,
                          boxShadow: "none",
                        }}
                      >
                        <CardBody className="py-2 px-2">
                          <div
                            className="d-flex align-items-center mb-2"
                            style={{ gap: 6 }}
                          >
                            <span
                              style={{
                                fontWeight: 700,
                                fontSize: 13,
                                color: getTextColor(),
                                textTransform: "uppercase",
                              }}
                            >
                              {item.display_name || item.name || "-"}
                            </span>
                            {canEditWorksheet && !isDisabled && (
                              <Button
                                color="link"
                                className="btn-icon btn-link p-0"
                                onClick={() => handleRemoveSelectedAccessory(idx)}
                                style={{ color: activeColorStyle.borderColor }}
                              >
                                <i className="tim-icons icon-trash-simple" />
                              </Button>
                            )}
                          </div>
                          <Row className="mx-0">
                            <Col md="6" className="pl-0 pr-1">
                              <FormGroup className="mb-1">
                                <Label className="form-control-label mb-0" style={{ fontSize: 12 }}>
                                  Price:
                                </Label>
                                <DollarInput
                                  value={item.price}
                                  onChange={(value) =>
                                    handleSelectedAccessoryChange(idx, "price", value)
                                  }
                                  placeholder="$0.00"
                                  disabled={isDisabled || !canEditWorksheet}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6" className="pr-0 pl-1">
                              <FormGroup className="mb-1">
                                <Label className="form-control-label mb-0" style={{ fontSize: 12 }}>
                                  Cost:
                                </Label>
                                <DollarInput
                                  value={item.cost}
                                  onChange={(value) =>
                                    handleSelectedAccessoryChange(idx, "cost", value)
                                  }
                                  placeholder="$0.00"
                                  disabled={isDisabled || !canEditWorksheet}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <Row className="mx-0">
                            <Col md="12" className="px-0">
                              <FormGroup className="mb-0">
                                <Label className="form-control-label mb-0" style={{ fontSize: 12 }}>
                                  Description:
                                </Label>
                                <Input
                                  type="text"
                                  value={item.description || ""}
                                  onChange={(e) =>
                                    handleSelectedAccessoryChange(
                                      idx,
                                      "description",
                                      e.target.value
                                    )
                                  }
                                  disabled={isDisabled || !canEditWorksheet}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    ))}
                  </Card>
                </TabPane>
              </TabContent>
              {formValues.type !== "wholesale" && (
                <>
                  <Nav tabs className="border-0" style={{ borderBottom: "none" }}>
                    {[
                      { id: "LENDER_FEES", label: "Lender Fees & Decision" },
                      { id: "COMMISSIONS", label: "Commissions" },
                      { id: "WASHOUT", label: "Washout" },
                      { id: "AMORTIZATION", label: "Amortization" }
                    ].map(tab => (
                      <NavItem key={tab.id}>
                        <NavLink
                          href="#"
                          className={activeMainTab === tab.id ? "active font-weight-bold" : "text-muted"}
                          style={{
                            background: "none",
                            border: "none",
                            // fontSize: 16,
                            // marginRight: 8,
                            position: "relative",
                            // fontWeight: 500,
                            cursor: 'pointer',
                            color: activeMainTab === tab.id ? activeColorStyle.borderColor : undefined,
                            fontWeight: activeMainTab === tab.id ? 700 : 500
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            setActiveMainTab(tab.id);
                          }}
                        >
                          {tab.label}
                          {activeMainTab === tab.id && (
                            <div style={{ height: 3, background: activeColorStyle.borderColor, borderRadius: 2, position: "absolute", left: 0, right: 0, bottom: -8 }} />
                          )}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>
                  <Card className="mb-0" style={getCardShadowStyle()}>
                    <CardBody>
                      <TabContent activeTab={activeMainTab}>
                        <TabPane tabId="LENDER_FEES">
                          <Col md="12">
                            <Nav tabs className="border-0" style={{ borderBottom: "none", paddingTop: 0 }}>
                              {["DISCOUNT", "USE RESERVE"].map((tab) => (
                                <NavItem key={tab}>
                                  <NavLink
                                    href="#"
                                    className={activeLenderFeeTab === tab ? "active font-weight-bold" : "text-muted"}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      // fontSize: 16,
                                      // marginRight: 8,
                                      position: "relative",
                                      // fontWeight: 500,
                                      cursor: 'pointer',
                                      color: activeLenderFeeTab === tab ? activeColorStyle.borderColor : undefined,
                                      fontWeight: activeLenderFeeTab === tab ? 700 : 500
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setActiveLenderFeeTab(tab);
                                    }}
                                  >
                                    {tab}
                                    {activeLenderFeeTab === tab && (
                                      <div style={{ height: 3, background: activeColorStyle.borderColor, borderRadius: 2, position: "absolute", left: 0, right: 0, bottom: -8 }} />
                                    )}
                                  </NavLink>
                                </NavItem>
                              ))}
                            </Nav>
                            <TabContent activeTab={activeLenderFeeTab}>
                              <TabPane tabId="DISCOUNT">
                                <Row>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label for="escrow_fee">Escrow Fee</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                        type="text"
                                        name="escrow_fee"
                                        id="escrow_fee"
                                        value={formValues.lender?.escrow_fee || ''}
                                        onChange={(e) => setFormValues(prev => ({ ...prev, lender: { ...prev.lender, escrow_fee: e.target.value } }))}
                                        onBlur={(e) => {
                                          setFormValues(prev => ({
                                            ...prev,
                                            lender: { ...prev.lender, escrow_fee: formatPriceWithDollar(formValues.lender?.escrow_fee || '0') }
                                          }));
                                        }}
                                        onFocus={(e) => {
                                          const raw = (formValues.lender?.escrow_fee || '0').toString().replace(/[^\d.]/g, '');
                                          setFormValues(prev => ({ ...prev, lender: { ...prev.lender, escrow_fee: raw } }));
                                        }}
                                        placeholder="0.00"
                                        disabled={isDisabled}
                                      />
                                    </FormGroup>
                                  </Col>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label for="loan_acq_fee">Loan Acq Fee</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                        type="text"
                                        name="loan_acq_fee"
                                        id="loan_acq_fee"
                                        value={formValues.lender?.loan_acq_fee || ''}
                                        onChange={(e) => setFormValues(prev => ({ ...prev, lender: { ...prev.lender, loan_acq_fee: e.target.value } }))}
                                        onBlur={(e) => {
                                          setFormValues(prev => ({
                                            ...prev,
                                            lender: { ...prev.lender, loan_acq_fee: formatPriceWithDollar(formValues.lender?.loan_acq_fee || '0') }
                                          }));
                                        }}
                                        onFocus={(e) => {
                                          const raw = (formValues.lender?.loan_acq_fee || '0').toString().replace(/[^\d.]/g, '');
                                          setFormValues(prev => ({ ...prev, lender: { ...prev.lender, loan_acq_fee: raw } }));
                                        }}
                                        placeholder="0.00"
                                        disabled={isDisabled}
                                      />
                                    </FormGroup>
                                  </Col>
                                </Row>
                                <Row>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label for="flat_discount">Flat Discount</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                        type="text"
                                        name="flat_discount"
                                        id="flat_discount"
                                        value={formValues.lender?.flat_discount || ''}
                                        onChange={(e) => setFormValues(prev => ({ ...prev, lender: { ...prev.lender, flat_discount: e.target.value } }))}
                                        onBlur={(e) => {
                                          setFormValues(prev => ({
                                            ...prev,
                                            lender: { ...prev.lender, flat_discount: formatPriceWithDollar(formValues.lender?.flat_discount || '0') }
                                          }));
                                        }}
                                        onFocus={(e) => {
                                          const raw = (formValues.lender?.flat_discount || '0').toString().replace(/[^\d.]/g, '');
                                          setFormValues(prev => ({ ...prev, lender: { ...prev.lender, flat_discount: raw } }));
                                        }}
                                        placeholder="0.00"
                                        disabled={isDisabled}
                                      />
                                    </FormGroup>
                                  </Col>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label for="discount_percent">Discount %</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                        type="text"
                                        name="discount_percent"
                                        id="discount_percent"
                                        value={formValues.lender?.discount_percent ? formValues.lender?.discount_percent + '%' : ''}
                                        onChange={(e) => {
                                          const value = Math.min(100, Math.max(0, parseFloat(e.target.value.replace('%', '')) || 0));
                                          setFormValues(prev => ({ ...prev, lender: { ...prev.lender, discount_percent: value } }));
                                        }}
                                        placeholder="0.0000%"
                                        disabled={isDisabled}
                                      />
                                    </FormGroup>
                                  </Col>
                                </Row>
                              </TabPane>
                              <TabPane tabId="USE RESERVE">
                                <Row>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label for="dealer_reserve">Dealer Reserve?</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                        type="select"
                                        name="dealer_reserve"
                                        id="dealer_reserve"
                                        value={formValues.lender?.dealer_reserve}
                                        onChange={(e) => setFormValues(prev => ({ ...prev, lender: { ...prev.lender, dealer_reserve: e.target.value } }))}
                                        disabled={isDisabled}
                                      >
                                        <option value="No">No</option>
                                        <option value="Yes">Yes</option>
                                      </Input>
                                    </FormGroup>
                                  </Col>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label for="flat_reserve">Flat Reserve</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                        type="text"
                                        name="flat_reserve"
                                        id="flat_reserve"
                                        value={formValues.lender?.flat_reserve || ''}
                                        disabled={isDisabled}
                                        onChange={(e) => setFormValues(prev => ({ ...prev, lender: { ...prev.lender, flat_reserve: e.target.value } }))}
                                        onBlur={(e) => {
                                          setFormValues(prev => ({
                                            ...prev,
                                            lender: { ...prev.lender, flat_reserve: formatPriceWithDollar(formValues.lender?.flat_reserve || '0') }
                                          }));
                                        }}
                                        onFocus={(e) => {
                                          const raw = (formValues.lender?.flat_reserve || '0').toString().replace(/[^\d.]/g, '');
                                          setFormValues(prev => ({ ...prev, lender: { ...prev.lender, flat_reserve: raw } }));
                                        }}
                                        placeholder="0.00"
                                      />
                                    </FormGroup>
                                  </Col>
                                </Row>
                                <Row>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label for="buy_rate_percent">Buy Rate %</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                        type="text"
                                        name="buy_rate_percent"
                                        id="buy_rate_percent"
                                        value={formValues.lender?.buy_rate_percent ? `${formValues.lender?.buy_rate_percent}%` : ''}
                                        onChange={(e) => {
                                          let value = e.target.value.replace(/[^\d.]/g, '');

                                          // Ensure only one decimal point
                                          const parts = value.split('.');
                                          if (parts.length > 2) {
                                            value = parts[0] + '.' + parts.slice(1).join('');
                                          }

                                          // Limit to 100% max
                                          const numValue = Math.min(100, Math.max(0, parseFloat(value) || 0));
                                          setFormValues(prev => ({ ...prev, lender: { ...prev.lender, buy_rate_percent: numValue } }));
                                        }}
                                        placeholder="0.0000%"
                                        disabled={isDisabled}
                                      />
                                    </FormGroup>
                                  </Col>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label for="dlr_reserve_retain_percent">Dlr Reserve Retain %</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                        type="text"
                                        name="dlr_reserve_retain_percent"
                                        id="dlr_reserve_retain_percent"
                                        value={formValues.lender?.dlr_reserve_retain_percent ? `${formValues.lender?.dlr_reserve_retain_percent}%` : ''}
                                        disabled={isDisabled}
                                        onChange={(e) => {
                                          let value = e.target.value.replace(/[^\d.]/g, '');

                                          // Ensure only one decimal point
                                          const parts = value.split('.');
                                          if (parts.length > 2) {
                                            value = parts[0] + '.' + parts.slice(1).join('');
                                          }

                                          // Limit to 100% max
                                          const numValue = Math.min(100, Math.max(0, parseFloat(value) || 0));
                                          setFormValues(prev => ({ ...prev, lender: { ...prev.lender, dlr_reserve_retain_percent: numValue } }));
                                        }}
                                        placeholder="0.0000%"
                                      />
                                    </FormGroup>
                                  </Col>

                                  {/* <Col md="6">
                                <FormGroup>
                                  <Label>&nbsp;</Label>
                                  <div style={{ fontWeight: 700, color: getTextColor() }}>{formatPriceWithDollar(formValues.lender?.dealer_reserve_amount)}</div>
                                </FormGroup>
                              </Col> */}
                                </Row>
                              </TabPane>
                            </TabContent>
                          </Col>
                        </TabPane>

                        <TabPane tabId="COMMISSIONS">
                          <Row className="mt-3">
                            <Col md="6">
                              <Col md="12">
                                <FormGroup>
                                  <Label for="finder_fee">Finder Fee</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                    type="text"
                                    name="finder_fee"
                                    id="finder_fee"
                                    value={formValues?.commissions?.finder_fee}
                                    disabled={isDisabled}
                                    onChange={(e) => {
                                      setFormValues(prev => ({
                                        ...prev,
                                        commissions: { ...prev.commissions, finder_fee: e.target.value }
                                      }));
                                    }}
                                    onBlur={(e) => {
                                      setFormValues(prev => ({
                                        ...prev,
                                        commissions: { ...prev.commissions, finder_fee: formatPriceWithDollar(formValues?.commissions?.finder_fee) }
                                      }));
                                    }}
                                    onFocus={(e) => {
                                      const raw = formValues?.commissions?.finder_fee.toString().replace(/[^\d.]/g, '');
                                      setFormValues(prev => ({
                                        ...prev, commissions: { ...prev.commissions, finder_fee: raw }
                                      }));
                                    }}
                                    placeholder="0.00"
                                  />
                                </FormGroup>
                              </Col>
                              <Col md="12">
                                <Row style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Col md="7">
                                    <FormGroup>
                                      <Label for="sales_gross_percent">Sales Gross %</Label>
                                      <Input
                                        className="form-control"
                                        style={inputStyle14}
                                        type="text"
                                        name="sales_gross_percent"
                                        id="sales_gross_percent"
                                        value={formValues?.commissions?.sales_gross_percent ? formValues?.commissions?.sales_gross_percent + '%' : ''}
                                        onChange={(e) => {
                                          const value = Math.min(100, Math.max(0, parseFloat(e.target.value.replace('%', '')) || 0));
                                          setFormValues(prev => ({
                                            ...prev,
                                            commissions: { ...prev.commissions, sales_gross_percent: value }
                                          }));
                                        }}
                                        placeholder="0.0000%"
                                        disabled={isDisabled} />
                                    </FormGroup>
                                  </Col>
                                  <Col style={{ textAlign: 'right' }} md="5">
                                    <h5 style={{ fontWeight: 700, color: getTextColor() }}>{formValues?.commissions?.sales_gross_percent == 0 ? 0 : formatPriceWithDollar(toNumber(formValues.sales_price) * toNumber(formValues?.commissions?.sales_gross_percent) / 100 - toNumber(formValues?.commissions?.finder_fee))}</h5>
                                  </Col>
                                </Row>
                              </Col>
                              <Col md="12">
                                <Row style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label for="front_end_percent">Front End %</Label>
                                      <Input
                                        className="form-control"
                                        style={inputStyle14}
                                        type="text"
                                        name="front_end_percent"
                                        id="front_end_percent"
                                        value={formValues?.commissions?.front_end_percent ? formValues?.commissions?.front_end_percent + '%' : ''}
                                        onChange={(e) => {
                                          const value = Math.min(100, Math.max(0, parseFloat(e.target.value.replace('%', '')) || 0));
                                          setFormValues(prev => ({
                                            ...prev,
                                            commissions: { ...prev.commissions, front_end_percent: value }
                                          }));
                                        }}
                                        placeholder="0.0000%"
                                        disabled={isDisabled} />
                                    </FormGroup>
                                  </Col>
                                  <Col style={{ textAlign: 'right' }} md="6">
                                    <h5 style={{ fontWeight: 700, color: getTextColor() }}>{formValues?.commissions?.front_end_percent == 0 ? 0 : formatPriceWithDollar(toNumber(formValues.sales_price) * toNumber(formValues?.commissions?.front_end_percent) / 100)}</h5>
                                  </Col>
                                </Row>
                              </Col>
                              <Col md="12">
                                <Row style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label for="down_pay_percent">Down Pay %</Label>
                                      <Input
                                        className="form-control"
                                        style={inputStyle14}
                                        type="text"
                                        name="down_pay_percent"
                                        id="down_pay_percent"
                                        value={formValues?.commissions?.down_pay_percent ? formValues?.commissions?.down_pay_percent + '%' : ''}
                                        onChange={(e) => {
                                          const value = Math.min(100, Math.max(0, parseFloat(e.target.value.replace('%', '')) || 0));
                                          setFormValues(prev => ({
                                            ...prev,
                                            commissions: { ...prev.commissions, down_pay_percent: value }
                                          }));
                                        }}
                                        placeholder="0.0000%"
                                        disabled={isDisabled} />
                                    </FormGroup>
                                  </Col>
                                  <Col style={{ textAlign: 'right' }} md="6">
                                    <h5 style={{ fontWeight: 700, color: getTextColor() }}>{formValues?.commissions?.down_pay_percent == 0 ? 0 : formatPriceWithDollar(toNumber(formValues.sales_price) * toNumber(formValues?.commissions?.down_pay_percent) / 100)}</h5>
                                  </Col>
                                </Row>
                              </Col>
                              <Col md="12">
                                <Row style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label for="dealer_reserve_comm_percent">Dlr Resv Comm %</Label>
                                      <Input
                                        className="form-control"
                                        style={inputStyle14}
                                        type="text"
                                        name="dealer_reserve_comm_percent"
                                        id="dealer_reserve_comm_percent"
                                        value={formValues?.commissions?.dealer_reserve_comm_percent ? formValues?.commissions?.dealer_reserve_comm_percent + '%' : ''}
                                        onChange={(e) => {
                                          const value = Math.min(100, Math.max(0, parseFloat(e.target.value.replace('%', '')) || 0));
                                          setFormValues(prev => ({
                                            ...prev,
                                            commissions: { ...prev.commissions, dealer_reserve_comm_percent: value }
                                          }));
                                        }}
                                        placeholder="0.0000%"
                                        disabled={isDisabled} />
                                    </FormGroup>
                                  </Col>
                                  <Col style={{ textAlign: 'right' }} md="6">
                                    <h5 style={{ fontWeight: 700, color: getTextColor() }}>{formValues?.commissions?.dealer_reserve_comm_percent == 0 ? 0 : formatPriceWithDollar(toNumber(formValues.sales_price) * toNumber(formValues?.commissions?.dealer_reserve_comm_percent) / 100)}</h5>
                                  </Col>
                                </Row>
                              </Col>
                            </Col>
                            <Col md="6">
                              <Col md="12">
                                <FormGroup>
                                  <Label for="pack_overhead">Pack (overhead)</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                    type="text"
                                    name="pack_overhead"
                                    id="pack_overhead"
                                    value={formValues?.commissions?.pack_overhead}
                                    disabled={isDisabled}
                                    onChange={(e) => {
                                      setFormValues(prev => ({
                                        ...prev,
                                        commissions: { ...prev.commissions, pack_overhead: e.target.value }
                                      }));
                                    }}
                                    onBlur={(e) => {
                                      setFormValues(prev => ({
                                        ...prev,
                                        commissions: { ...prev.commissions, pack_overhead: formatPriceWithDollar(formValues?.commissions?.pack_overhead) }
                                      }));
                                    }}
                                    onFocus={(e) => {
                                      const raw = formValues?.commissions?.pack_overhead.toString().replace(/[^\d.]/g, '');
                                      setFormValues(prev => ({
                                        ...prev, commissions: { ...prev.commissions, pack_overhead: raw }
                                      }));
                                    }}
                                    placeholder="0.00"
                                  />
                                </FormGroup>
                              </Col>
                              <Col md="12">
                                <Row style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label for="comm_gross_percent">Comm Gross % </Label>
                                      <Input
                                        className="form-control"
                                        style={inputStyle14}
                                        type="text"
                                        name="comm_gross_percent"
                                        id="comm_gross_percent"
                                        value={formValues?.commissions?.comm_gross_percent ? formValues?.commissions?.comm_gross_percent + '%' : ''}
                                        onChange={(e) => {
                                          const value = Math.min(100, Math.max(0, parseFloat(e.target.value.replace('%', '')) || 0));
                                          setFormValues(prev => ({
                                            ...prev,
                                            commissions: { ...prev.commissions, comm_gross_percent: value }
                                          }));
                                        }}
                                        placeholder="0.0000%"
                                        disabled={isDisabled} />
                                    </FormGroup>
                                  </Col>
                                  <Col style={{ textAlign: 'right' }} md="6">
                                    <h5 style={{ fontWeight: 700, color: getTextColor() }}>{formValues?.commissions?.comm_gross_percent == 0 ? 0 : formatPriceWithDollar((toNumber(formValues.sales_price) * toNumber(formValues?.commissions?.comm_gross_percent) / 100) - (parseFloat(formValues?.commissions?.pack_overhead) || 0))}</h5>
                                  </Col>
                                </Row>
                              </Col>
                              <Col md="12">
                                <Row style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label for="back_end_percent">Back End %</Label>
                                      <Input
                                        className="form-control"
                                        style={inputStyle14}
                                        type="text"
                                        name="back_end_percent"
                                        id="back_end_percent"
                                        value={formValues?.commissions?.back_end_percent ? formValues?.commissions?.back_end_percent + '%' : ''}
                                        onChange={(e) => {
                                          const value = Math.min(100, Math.max(0, parseFloat(e.target.value.replace('%', '')) || 0));
                                          setFormValues(prev => ({
                                            ...prev,
                                            commissions: { ...prev.commissions, back_end_percent: value }
                                          }));
                                        }}
                                        placeholder="0.0000%"
                                        disabled={isDisabled} />
                                    </FormGroup>
                                  </Col>
                                  <Col style={{ textAlign: 'right' }} md="6">
                                    <h5 style={{ fontWeight: 700, color: getTextColor() }}>{formValues?.commissions?.back_end_percent == 0 ? 0 : formatPriceWithDollar(toNumber(formValues.sales_price) * toNumber(formValues?.commissions?.back_end_percent) / 100)}</h5>
                                  </Col>
                                </Row>
                              </Col>
                              <Col md="12">
                                <Row style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label for="def_down_pay_comm_percent">Def Down Pay Comm %</Label>
                                      <Input
                                        className="form-control"
                                        style={inputStyle14}
                                        type="text"
                                        name="def_down_pay_comm_percent"
                                        id="def_down_pay_comm_percent"
                                        value={formValues?.commissions?.def_down_pay_comm_percent ? formValues?.commissions?.def_down_pay_comm_percent + '%' : ''}
                                        onChange={(e) => {
                                          const value = Math.min(100, Math.max(0, parseFloat(e.target.value.replace('%', '')) || 0));
                                          setFormValues(prev => ({
                                            ...prev,
                                            commissions: { ...prev.commissions, def_down_pay_comm_percent: value }
                                          }));
                                        }}
                                        placeholder="0.0000%"
                                        disabled={isDisabled} />
                                    </FormGroup>
                                  </Col>
                                  <Col style={{ textAlign: 'right' }} md="6">
                                    <h5 style={{ fontWeight: 700, color: getTextColor() }}>{formValues?.commissions?.def_down_pay_comm_percent == 0 ? 0 : formatPriceWithDollar(toNumber(formValues.sales_price) * toNumber(formValues?.commissions?.def_down_pay_comm_percent) / 100)}</h5>
                                  </Col>
                                </Row>
                              </Col>
                              <Col md="12">
                                <FormGroup>
                                  <Label for="flat_commission">Flat Comm</Label>
                            <Input
                              className="form-control"
                              style={inputStyle14}
                                    type="text"
                                    name="flat_commission"
                                    id="flat_commission"
                                    value={formValues?.commissions?.flat_commission}
                                    disabled={isDisabled}
                                    onChange={(e) => {
                                      const raw = e.target.value.replace(/[^\d.]/g, '');
                                      setFormValues(prev => ({
                                        ...prev,
                                        commissions: { ...prev.commissions, flat_commission: raw }
                                      }));
                                    }}
                                    onBlur={(e) => {
                                      setFormValues(prev => ({
                                        ...prev,
                                        commissions: { ...prev.commissions, flat_commission: formatPriceWithDollar(formValues?.commissions?.flat_commission) }
                                      }));
                                    }}
                                    onFocus={(e) => {
                                      const raw = (formValues?.commissions?.flat_commission || '').toString().replace(/[^\d.]/g, '');
                                      setFormValues(prev => ({ ...prev, commissions: { ...prev.commissions, flat_commission: raw } }));
                                    }}
                                    placeholder="0.00"
                                  />
                                </FormGroup>
                              </Col>
                            </Col>
                          </Row>
                        </TabPane>
                        <TabPane tabId="WASHOUT">
                          <Row className="mt-3" style={{ justifyContent: 'space-between' }}>
                            <Col md="12">
                              <div className="d-flex justify-content-between mb-2" style={{ fontWeight: 600, borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor(), fontSize: 16 }}>Sales Price</span><span style={{ color: getTextColor(), fontSize: 16 }}>{formatPriceWithDollar(worksheetData?.washout?.sales_price)}</span></div>
                              <div className="d-flex align-items-center mb-2" style={{ borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.4)' }}>
                                <span style={{ color: getTextColor(), flex: 1 }}>Trade-In</span>
                                <span style={{ color: getTextColor(), width: 20, textAlign: 'center', fontSize: 20 }}>-</span>
                                <span style={{ color: getTextColor(), flex: 1, textAlign: 'right' }}>{formatPriceWithDollar(worksheetData?.washout?.trade_in_total)}</span>
                              </div>
                              <div className="d-flex justify-content-between mb-2" style={{ fontWeight: 600, borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor(), fontSize: 16 }}>Sales Price Cash Diff</span><span style={{ color: getTextColor(), fontSize: 16 }}>{formatPriceWithDollar(worksheetData?.washout?.sales_price_cash_diff)}</span></div>
                              <div className="d-flex align-items-center mb-2" style={{ borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.4)' }}>
                                <span style={{ color: getTextColor(), flex: 1 }}>Trade-In ACV</span>
                                <span style={{ color: getTextColor(), width: 20, textAlign: 'center', fontSize: 20 }}>+</span>
                                <span style={{ color: getTextColor(), flex: 1, textAlign: 'right' }}>{formatPriceWithDollar(worksheetData?.washout?.trade_in_acv)}</span>
                              </div>
                              <div className="d-flex justify-content-between mb-2" style={{ fontWeight: 600, borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor(), fontSize: 16 }}>Sales Draw</span><span style={{ color: getTextColor(), fontSize: 16 }}>{formatPriceWithDollar(worksheetData?.washout?.sales_draw)}</span></div>
                              <div className="d-flex align-items-center mb-2" >
                                <span style={{ color: getTextColor(), flex: 1 }}>Total Vehicle Cost</span>
                                <span style={{ color: getTextColor(), width: 20, textAlign: 'center', fontSize: 20 }}>-</span>
                                <span style={{ color: getTextColor(), flex: 1, textAlign: 'right' }}>{formatPriceWithDollar(worksheetData?.washout?.total_vehicle_cost)}</span>
                              </div>
                              <div className="d-flex align-items-center mb-2" >
                                <span style={{ color: getTextColor(), flex: 1 }}>Finders Fee</span>
                                <span style={{ color: getTextColor(), width: 20, textAlign: 'center', fontSize: 20 }}>-</span>
                                <span style={{ color: getTextColor(), flex: 1, textAlign: 'right' }}>{formatPriceWithDollar(worksheetData?.washout?.finder_fee)}</span>
                              </div>
                              <div className="d-flex align-items-center mb-2" >
                                <span style={{ color: getTextColor(), flex: 1 }}>Escrow Fee</span>
                                <span style={{ color: getTextColor(), width: 20, textAlign: 'center', fontSize: 20 }}>-</span>
                                <span style={{ color: getTextColor(), flex: 1, textAlign: 'right' }}>{formatPriceWithDollar(worksheetData?.washout?.escrow_fee)}</span>
                              </div>
                              <div className="d-flex align-items-center mb-2" >
                                <span style={{ color: getTextColor(), flex: 1 }}>Acq Fee</span>
                                <span style={{ color: getTextColor(), width: 20, textAlign: 'center', fontSize: 20 }}>-</span>
                                <span style={{ color: getTextColor(), flex: 1, textAlign: 'right' }}>{formatPriceWithDollar(worksheetData?.washout?.loan_acq_fee)}</span>
                              </div>
                              <div className="d-flex align-items-center mb-2" style={{ borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.4)' }}>
                                <span style={{ color: getTextColor(), flex: 1 }}>Total Discount</span>
                                <span style={{ color: getTextColor(), width: 20, textAlign: 'center', fontSize: 20 }}>-</span>
                                <span style={{ color: getTextColor(), flex: 1, textAlign: 'right' }}>{formatPriceWithDollar(worksheetData?.washout?.total_discount)}</span>
                              </div>
                            </Col>
                            <Col md="12">
                              <div className="d-flex justify-content-between mb-2" style={{ fontWeight: 600, borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor(), fontSize: 16 }}>Sales Gross Profit</span><span style={{ color: getTextColor(), fontSize: 16 }}>{formatPriceWithDollar(worksheetData?.washout?.sales_gross_profit)}</span></div>
                              <div className="d-flex align-items-center mb-2" style={{ borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.4)' }}>
                                <span style={{ color: getTextColor(), flex: 1 }}>Pack Fee</span>
                                <span style={{ color: getTextColor(), width: 20, textAlign: 'center', fontSize: 20 }}>-</span>
                                <span style={{ color: getTextColor(), flex: 1, textAlign: 'right' }}>{formatPriceWithDollar(worksheetData?.washout?.pack_fee)}</span>
                              </div>
                              <div className="d-flex justify-content-between mb-2" style={{ fontWeight: 600, borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor(), fontSize: 16 }}>Comm Gross Profit</span><span style={{ color: getTextColor(), fontSize: 16 }}>{formatPriceWithDollar(worksheetData?.washout?.comm_gross_profit)}</span></div>
                              <div className="d-flex align-items-center mb-2" >
                                <span style={{ color: getTextColor(), flex: 1 }}>Accessory Prices</span>
                                <span style={{ color: getTextColor(), width: 20, textAlign: 'center', fontSize: 20 }}>+</span>
                                <span style={{ color: getTextColor(), flex: 1, textAlign: 'right' }}>{formatPriceWithDollar(worksheetData?.washout?.accessory_prices)}</span>
                              </div>
                              <div className="d-flex align-items-center mb-2" style={{ borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.4)' }}>
                                <span style={{ color: getTextColor(), flex: 1 }}>Accessory Costs</span>
                                <span style={{ color: getTextColor(), width: 20, textAlign: 'center', fontSize: 20 }}>-</span>
                                <span style={{ color: getTextColor(), flex: 1, textAlign: 'right' }}>{formatPriceWithDollar(0)}</span>
                              </div>
                              <div className="d-flex justify-content-between mb-2" style={{ fontWeight: 600, borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor(), fontSize: 16 }}>Front End Profit</span><span style={{ color: getTextColor(), fontSize: 16 }}>{formatPriceWithDollar(worksheetData?.washout?.front_end_profit)}</span></div>
                              <div className="d-flex align-items-center mb-2" >
                                <span style={{ color: getTextColor(), flex: 1 }}>After Market Prices</span>
                                <span style={{ color: getTextColor(), width: 20, textAlign: 'center', fontSize: 20 }}>+</span>
                                <span style={{ color: getTextColor(), flex: 1, textAlign: 'right' }}>{formatPriceWithDollar(worksheetData?.washout?.after_market_prices)}</span>
                              </div>
                              <div className="d-flex align-items-center mb-2" style={{ borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.4)' }}>
                                <span style={{ color: getTextColor(), flex: 1 }}>After Market Costs</span>
                                <span style={{ color: getTextColor(), width: 20, textAlign: 'center', fontSize: 20 }}>-</span>
                                <span style={{ color: getTextColor(), flex: 1, textAlign: 'right' }}>{formatPriceWithDollar(worksheetData?.washout?.after_market_costs)}</span>
                              </div>
                              <div className="d-flex justify-content-between mb-2" style={{ fontWeight: 600, borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor(), fontSize: 16 }}>Back End Profit</span><span style={{ color: getTextColor(), fontSize: 16 }}>{formatPriceWithDollar(worksheetData?.washout?.back_end_profit)}</span></div>
                              <div className="d-flex align-items-center mb-2" >
                                <span style={{ color: getTextColor(), flex: 1 }}>Dealer Reserves</span>
                                <span style={{ color: getTextColor(), width: 20, textAlign: 'center', fontSize: 20 }}>+</span>
                                <span style={{ color: getTextColor(), flex: 1, textAlign: 'right' }}>{formatPriceWithDollar(worksheetData?.washout?.dealer_reserves)}</span>
                              </div>
                            </Col>
                          </Row>
                          <Row className="mt-2">
                            <Col md="12">
                              <div className="d-flex align-items-center mb-2" style={{ borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.4)' }}>
                                <span style={{ color: getTextColor(), flex: 1 }}>Doc Fee</span>
                                <span style={{ color: getTextColor(), width: 20, textAlign: 'center', fontSize: 20 }}>+</span>
                                <span style={{ color: getTextColor(), flex: 1, textAlign: 'right' }}>{formatPriceWithDollar(worksheetData?.washout?.doc_fee)}</span>
                              </div>
                              <div className="d-flex justify-content-between mb-2" style={{ fontWeight: 600, borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor(), fontSize: 16 }}>Total Deal Gross Profit</span><span style={{ color: getTextColor(), fontSize: 16 }}>{formatPriceWithDollar(worksheetData?.washout?.total_deal_gross_profit)}</span></div>
                            </Col>
                            <Col md="12">
                              <div className="d-flex align-items-center mb-2" style={{ borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.4)' }}>
                                <span style={{ color: getTextColor(), flex: 1 }}>Total Commission Paid</span>
                                <span style={{ color: getTextColor(), width: 20, textAlign: 'center', fontSize: 20 }}>-</span>
                                <span style={{ color: getTextColor(), flex: 1, textAlign: 'right' }}>{formatPriceWithDollar(worksheetData?.washout?.total_commisson_paid)}</span>
                              </div>
                              <div className="d-flex justify-content-between mb-2" style={{ borderBottom: currentTheme ? '2px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor(), fontWeight: 700 }}>Total Deal Net Profit</span><span style={{ color: getTextColor(), fontWeight: 700 }}>{formatPriceWithDollar(worksheetData?.washout?.total_deal_net_profit)}</span></div>
                            </Col>
                          </Row>
                        </TabPane>

                        <TabPane tabId="AMORTIZATION">
                          <Table responsive>
                            <thead className="text-primary">
                              <tr>
                                <th>Pay #</th>
                                <th>Date</th>
                                <th>Total Pmt</th>
                                <th>Int Paid</th>
                                <th>Prin Paid</th>
                                <th>End Balance</th>
                              </tr>
                            </thead>
                            <tbody style={{ color: getTextColor() }}>
                              <tr>
                                <td>1</td>
                                <td>{nextDayFromContract()}</td>
                                <td>{formatPriceWithDollar(worksheetData?.washout?.total_sale)}</td>
                                <td>{formatPriceWithDollar(0)}</td>
                                <td>{formatPriceWithDollar(worksheetData?.washout?.total_sale)}</td>
                                <td>{formatPriceWithDollar(0)}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </TabPane>
                      </TabContent>
                    </CardBody>
                  </Card>
                </>
              )}
            </Col>
            <Col md="6" >
              <Card className="mb-2" style={getCardShadowStyle()}>
                <CardBody>
                  {addedVehicles.length === 0 ? (
                    <div className="d-flex flex-row align-items-center justify-content-between">
                      <img src={require("assets/img/car.png")} alt="No vehicle" style={{ width: 100, height: 100, objectFit: 'contain' }} />
                      <div className="d-flex flex-column align-items-center justify-content-center">
                        <div className="text-muted">No vehicle has been added to this deal.</div>
                        <Button size="sm" style={{ ...activeColorStyle, color: 'white' }} onClick={handleAddVehicleClick}>
                          Add a Vehicle
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 style={{ color: getTextColor(), margin: 0, fontSize: 12 }}>Vehicles in Deal</h6>
                      </div>
                      {addedVehicles.map((vehicle, index) => (
                        <div
                          key={vehicle.id}
                          className="mb-3 p-3"
                          style={{
                            cursor: "pointer",
                            border: currentTheme ? '1px solid rgba(255,255,255,0.2)' : '1px solid #e0e0e0',
                            backgroundColor: currentTheme ? 'rgba(255,255,255,0.05)' : '#f8f9fa',
                            display: "flex",
                            flexDirection: "row",
                            gap: "10px"
                          }}
                        >
                          <SecureMediaImg
                            src={vehicle?.vehicle_image || ""}
                            alt=""
                            className="card-img-top"
                            style={{ objectFit: "contain", width: "150px" }}
                            enabled={!!vehicle?.vehicle_image}
                            fallback={
                              <img
                                src={require("assets/img/car.png")}
                                className="card-img-top"
                                alt=""
                                style={{ objectFit: "contain", width: "150px" }}
                              />
                            }
                          />
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            <div className="d-flex flex-row justify-content-between align-items-start" >
                              <h6 style={{ color: getTextColor(), margin: 0, fontSize: 12, textTransform: "math-auto" }}>
                                {vehicle.year} {vehicle.make} {vehicle.model}
                              </h6>
                            </div>

                            <div style={{ color: getTextColor(), fontSize: '14px' }}>
                              {vehicle.stockNumber && <div className="mb-1" style={{ fontSize: 12 }}>Stock: {vehicle.stockNumber}</div>}
                              {vehicle.trim && <div className="mb-1" style={{ fontSize: 12 }}>Trim: <span style={{ fontSize: 13 }}>{vehicle.trim}</span></div>}
                              {vehicle.vin && (
                                <div className="mb-1 d-flex align-items-center" style={{ fontSize: 12, gap: 8 }}>
                                  <span>
                                    VIN: <span style={{ fontSize: 13 }}>{vehicle.vin}</span>
                                  </span>
                                  {vehicle.vin && (
                                    <Button
                                      color="link"
                                      size="sm"
                                      className="p-0"
                                      style={{ cursor: "pointer", flexShrink: 0, lineHeight: 1 }}
                                      onClick={() => {
                                        navigator.clipboard.writeText(vehicle.vin);
                                        showNotification("success", "VIN copied to clipboard");
                                      }}
                                    >
                                      <i
                                        className="fa fa-copy"
                                        style={{ fontSize: 13, color: getTextColor() }}
                                      />
                                    </Button>
                                  )}
                                </div>
                              )}
                              {vehicle.status && (
                                <div className="mb-1" style={{ fontSize: 12 }}>
                                  Status:{" "}
                                  <span
                                    style={{
                                      color:
                                        String(vehicle.status).trim().toLowerCase() === "sold"
                                          ? "#dc3545"
                                          : vehicle?.status_color || undefined,
                                      fontSize: 13,
                                      fontWeight:
                                        String(vehicle.status).trim().toLowerCase() === "sold"
                                          ? "bold"
                                          : undefined,
                                    }}
                                  >
                                    {String(vehicle.status).charAt(0).toUpperCase() +
                                      String(vehicle.status).slice(1)}
                                  </span>
                                </div>
                              )}
                              {vehicle?.mileage && <div className="mb-1" style={{ fontSize: 12 }}>Mileage: <span style={{ fontSize: 13 }}>{vehicle.mileage}</span></div>}
                              {vehicle.color && <div className="mb-1" style={{ fontSize: 12 }}>Color: <span style={{ fontSize: 13 }}>{vehicle.color}</span></div>}
                              {vehicle.sellingPrice && <div className="mb-1" style={{ fontSize: 12 }}>Price: <span style={{ fontSize: 13 }}>${vehicle.sellingPrice}</span></div>}
                            </div>
                            {!worksheetData?.inventory &&
                              <Button
                                style={{ ...activeColorStyle, color: 'white' }}
                                color="danger"
                                size="sm"
                                onClick={() => {
                                  setAddedVehicles(prev => prev.filter(v => v.id !== vehicle.id));
                                }}
                              >
                                Remove
                              </Button>
                            }
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
              <Card >
                <CardBody style={{ padding: 10, paddingBottom: "10px !important" }}>
                  <Row className="d-flex flex-row align-items-center justify-content-between">
                    <Col md="2" className="text-center" >
                      <div className="text-muted">APR</div>
                      <div style={{ fontWeight: 700, color: getTextColor() }}>{(worksheetData?.washout?.apr || 0)?.toFixed(4)}%</div>
                    </Col>
                    <Col md="3" className="text-center" >
                      <div className="text-muted">Finance Charge</div>
                      <div style={{ fontWeight: 700, color: getTextColor() }}>{formatPriceWithDollar(worksheetData?.washout?.finance_charge)}</div>
                    </Col>
                    <Col md="3" className="text-center" >
                      <div className="text-muted">Amount Financed</div>
                      <div style={{ fontWeight: 700, color: getTextColor() }}>{formatPriceWithDollar(worksheetData?.washout?.amount_financed)}</div>
                    </Col>
                    <Col md="2" className="text-center" >
                      <div className="text-muted">Total Down</div>
                      <div style={{ fontWeight: 700, color: getTextColor() }}>{formatPriceWithDollar(worksheetData?.washout?.total_down)}</div>
                    </Col>
                    <Col md="2" className="text-center" >
                      <div className="text-muted">Total Sale</div>
                      <div style={{ fontWeight: 700, color: getTextColor() }}>{formatPriceWithDollar(worksheetData?.washout?.total_sale)}</div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
              <Card style={getCardShadowStyle()}>
                <CardBody>
                  <Row>
                    <Col md={formValues.type === "cash" || formValues.type === "wholesale" ? "12" : "6"}>
                      <CardTitle tag="h5" className="mb-3">Sales Summary</CardTitle>
                      <div className="d-flex justify-content-between mb-2" style={{ borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '0.5px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor() }}>Sales Price:</span><span style={{ color: getTextColor() }}>{formatPriceWithDollar(worksheetData?.sales_price)}</span></div>
                      {/* Fees Popover Target */}
                      <div
                        id="fees-popover-target"
                        className="d-flex justify-content-between mb-2"
                        style={{
                          borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '0.5px solid rgba(0,0,0,0.4)',
                          cursor: 'pointer'
                        }}
                        onClick={() => setFeesPopoverOpen(!feesPopoverOpen)}
                      >
                        <span style={{ color: activeColorStyle.borderColor, textDecoration: 'underline', fontWeight: 600 }}>Fees:</span>
                        <span style={{ color: getTextColor() }}>{formatPriceWithDollar(worksheetData?.washout?.fees)}</span>
                      </div>

                      {/* Custom Style for this Popover to override Bootstrap defaults */}
                      <style>
                        {`
                          .fees-custom-popover,
                          .taxes-custom-popover {
                            max-width: none !important;
                            width: auto !important;
                          }
                          @media (max-width: 576px) {
                            .fees-custom-popover,
                            .taxes-custom-popover {
                                width: 95% !important;
                                margin: 0 auto;
                            }
                          }
                          ${worksheetTaxFeePopoverCss}
                        `}
                      </style>

                      {/* Fees Popover */}
                      <Popover
                        placement="bottom"
                        isOpen={feesPopoverOpen}
                        target="fees-popover-target"
                        toggle={() => setFeesPopoverOpen(!feesPopoverOpen)}
                        trigger="click"
                        className={classNames("shadow-lg border-0 fees-custom-popover", popoverTheme.modifier)}
                      >
                        <PopoverHeader
                          tag="h4"
                          className="border-bottom-0 pt-3 pb-2 px-3"
                          style={{ backgroundColor: popoverTheme.headerBg, color: popoverTheme.text }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>Fees</span>
                            <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>{formatPriceWithDollar(worksheetData?.washout?.fees)}</span>
                          </div>
                        </PopoverHeader>
                        <PopoverBody className="p-0" style={{ backgroundColor: popoverTheme.bg }}>
                          <div className="px-3" style={{ width: '480px', maxWidth: '100%', maxHeight: '400px', overflowY: 'auto' }}>
                            {!showFeeForm && (
                              <Nav pills className="nav-fill mb-3 fees-popover-tabs" style={{ border: `1px solid ${popoverTheme.navBorder}`, padding: '2px', borderRadius: 0 }}>
                                <NavItem>
                                  <NavLink
                                    className={feesActiveTab === 'CONTRACT' ? 'active' : ''}
                                    onClick={() => setFeesActiveTab('CONTRACT')}
                                    style={getPopoverNavTabStyle(feesActiveTab === 'CONTRACT')}
                                  >
                                    CONTRACT
                                  </NavLink>
                                </NavItem>
                                <NavItem>
                                  <NavLink
                                    className={feesActiveTab === 'REGISTRATION' ? 'active' : ''}
                                    onClick={() => setFeesActiveTab('REGISTRATION')}
                                    style={getPopoverNavTabStyle(feesActiveTab === 'REGISTRATION')}
                                  >
                                    REGISTRATION
                                  </NavLink>
                                </NavItem>
                              </Nav>
                            )}

                            {showFeeForm ? (
                              <div className="pb-3">
                                <FormGroup>
                                  <Label style={{ fontSize: 13, fontWeight: 600, color: popoverTheme.label }}>Description</Label>
                                  <Input
                                    className="form-control"
                                    value={currentFee.description}
                                    onChange={(e) => setCurrentFee({ ...currentFee, description: e.target.value })}
                                    style={popoverInputStyle}
                                  />
                                </FormGroup>
                                <FormGroup>
                                  <Label style={{ fontSize: 13, fontWeight: 600, color: popoverTheme.label }}>Category</Label>
                                  <Select
                                    styles={popoverSelectStyles}
                                    menuPortalTarget={document.body}
                                    className="react-select"
                                    value={{ label: currentFee.category, value: currentFee.category }}
                                    options={feesActiveTab === 'REGISTRATION' ? [
                                      "Deputy Service Fee",
                                      "Document Fee",
                                      "Inspection Station Fee",
                                      "License Fee",
                                      "Plate Transfer Fee",
                                      "Registration Fee",
                                      "Service Fee",
                                      "State Inspection Fee",
                                      "Title Fee",
                                      "Transportation Fee"
                                    ].map(opt => ({ label: opt, value: opt })) : [
                                      "Document Fee",
                                      "Filing Fee",
                                      "Other"
                                    ].map(opt => ({ label: opt, value: opt }))}
                                    onChange={(option) => setCurrentFee({ ...currentFee, category: option.value })}
                                  />
                                </FormGroup>
                                <FormGroup>
                                  <Label style={{ fontSize: 13, fontWeight: 600, color: popoverTheme.label }}>Amount</Label>
                                  <Input
                                    className="form-control"
                                    value={currentFee.amount}
                                    onChange={(e) => setCurrentFee({ ...currentFee, amount: e.target.value })}
                                    onBlur={() => setCurrentFee({ ...currentFee, amount: formatPriceWithDollar(currentFee.amount) })}
                                    onFocus={() => {
                                      const raw = currentFee.amount.toString().replace(/[^\d.]/g, '');
                                      setCurrentFee({ ...currentFee, amount: raw });
                                    }}
                                    style={popoverInputStyle}
                                  />
                                </FormGroup>
                                <div className="d-flex justify-content-between mt-4">
                                  <Button
                                    outline
                                    color="secondary"
                                    style={{ width: '48%', textTransform: 'uppercase', fontWeight: 600, fontSize: '11px', whiteSpace: 'nowrap', padding: '8px 0' }}
                                    onClick={() => setShowFeeForm(false)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    outline
                                    style={{ width: '48%', textTransform: 'uppercase', fontWeight: 600, color: activeColorStyle.borderColor, borderColor: activeColorStyle.borderColor, fontSize: '11px', whiteSpace: 'nowrap', padding: '8px 0' }}
                                    onClick={() => handleSaveFee()}
                                  >
                                    Confirm
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <TabContent activeTab={feesActiveTab}>
                                <TabPane tabId="CONTRACT" className="pb-3">
                                  <div className="d-flex justify-content-between font-weight-bold mb-2 text-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    <span style={{}}>Description</span>
                                    <span style={{}}>Amount</span>
                                  </div>
                                  <div className="mb-3">
                                    {worksheetData?.taxAndFee && worksheetData.taxAndFee.length > 0 ? (
                                      worksheetData.taxAndFee
                                        .filter(item => item.type === 'fee-contract')
                                        .map((fee, index) => (
                                          <div key={getTaxFeeRowKey(fee) || index} className="d-flex justify-content-between align-items-center mb-2" style={{ borderBottom: `1px solid ${popoverTheme.rowBorder}`, paddingBottom: '8px' }}>
                                            <div className="d-flex align-items-center">
                                              <span style={{ color: activeColorStyle.borderColor, fontSize: 14, fontWeight: 500 }}>{fee.description || fee.category}</span>
                                              {!isDisabled && (
                                                <>
                                                  <i
                                                    className="fa fa-edit ml-2 text-muted"
                                                    style={{ fontSize: 14, cursor: 'pointer' }}
                                                    onClick={() => {
                                                      setCurrentFee({
                                                        description: fee.description || fee.category || "",
                                                        category: fee.category || "Document Fee",
                                                        amount: formatPriceWithDollar(fee.amount || 0),
                                                        local_id: fee.local_id,
                                                        id: fee.id,
                                                        documentId: fee.documentId
                                                      });
                                                      setShowFeeForm(true);
                                                    }}
                                                  ></i>
                                                  <i
                                                    className="fa fa-trash ml-2 text-muted"
                                                    style={{ fontSize: 14, cursor: 'pointer' }}
                                                    onClick={() => handleDeleteFee(fee)}
                                                  ></i>
                                                </>
                                              )}
                                            </div>
                                            <span style={{ fontWeight: 600, fontSize: 14, color: popoverTheme.text }}>{formatPriceWithDollar(fee.amount || '0')}</span>
                                          </div>
                                        ))
                                    ) : null}
                                  </div>

                                  {!isDisabled && (
                                    <>
                                      <Button
                                        outline
                                        color="info"
                                        size="sm"
                                        block
                                        className="mt-3 mb-2 font-weight-bold"
                                        style={{ borderStyle: 'dashed', borderWidth: '2px', textTransform: 'uppercase', fontSize: 12 }}
                                        onClick={() => {
                                          setCurrentFee({ description: "", category: "Document Fee", amount: "", id: null, local_id: null });
                                          setShowFeeForm(true);
                                        }}
                                      >
                                        + Add Contract Fee
                                      </Button>
                                      <Button
                                        outline
                                        color="secondary"
                                        size="sm"
                                        block
                                        className="text-muted border-0"
                                        style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}
                                        onClick={handleResetTaxAndFees}
                                      >
                                        Reset Default Fees and Taxes
                                      </Button>
                                    </>
                                  )}
                                </TabPane>
                                <TabPane tabId="REGISTRATION" className="pb-3">
                                  <div className="d-flex justify-content-between font-weight-bold mb-2 text-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    <span style={{}}>Description</span>
                                    <span style={{}}>Amount</span>
                                  </div>
                                  <div className="mb-3">
                                    {worksheetData?.taxAndFee && worksheetData.taxAndFee.filter(item => item.type === 'fee-registration').length > 0 ? (
                                      worksheetData.taxAndFee
                                        .filter(item => item.type === 'fee-registration')
                                        .map((fee, index) => (
                                          <div key={getTaxFeeRowKey(fee) || index} className="d-flex justify-content-between align-items-center mb-2" style={{ borderBottom: `1px solid ${popoverTheme.rowBorder}`, paddingBottom: '8px' }}>
                                            <div className="d-flex align-items-center">
                                              <span style={{ color: activeColorStyle.borderColor, fontSize: 14, fontWeight: 500 }}>{fee.description || fee.category}</span>
                                              {!isDisabled && (
                                                <>
                                                  <i
                                                    className="fa fa-edit ml-2 text-muted"
                                                    style={{ fontSize: 14, cursor: 'pointer' }}
                                                    onClick={() => {
                                                      setCurrentFee({
                                                        description: fee.description || fee.category || "",
                                                        category: fee.category || "Registration Fee",
                                                        amount: formatPriceWithDollar(fee.amount || 0),
                                                        local_id: fee.local_id,
                                                        id: fee.id,
                                                        documentId: fee.documentId
                                                      });
                                                      setShowFeeForm(true);
                                                    }}
                                                  ></i>
                                                  <i
                                                    className="fa fa-trash ml-2 text-muted"
                                                    style={{ fontSize: 14, cursor: 'pointer' }}
                                                    onClick={() => handleDeleteFee(fee)}
                                                  ></i>
                                                </>
                                              )}
                                            </div>
                                            <span style={{ fontWeight: 600, fontSize: 14, color: popoverTheme.text }}>{formatPriceWithDollar(fee.amount || '0')}</span>
                                          </div>
                                        ))
                                    ) : null}
                                  </div>

                                  {!isDisabled && (
                                    <>
                                      <Button
                                        outline
                                        color="info"
                                        size="sm"
                                        block
                                        className="mt-3 mb-2 font-weight-bold"
                                        style={{ borderStyle: 'dashed', borderWidth: '2px', textTransform: 'uppercase', fontSize: 12 }}
                                        onClick={() => {
                                          setCurrentFee({ description: "", category: "Registration Fee", amount: "", id: null, local_id: null });
                                          setShowFeeForm(true);
                                        }}
                                      >
                                        + Add Registration Fee
                                      </Button>
                                      <Button
                                        outline
                                        color="secondary"
                                        size="sm"
                                        block
                                        className="text-muted border-0"
                                        style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}
                                        onClick={handleResetTaxAndFees}
                                      >
                                        Reset Default Fees and Taxes
                                      </Button>
                                    </>
                                  )}
                                </TabPane>
                              </TabContent>
                            )}
                          </div>
                        </PopoverBody>
                      </Popover>

                      {/* Taxes Popover Target */}
                      <div
                        id="taxes-popover-target"
                        className="d-flex justify-content-between mb-2"
                        style={{
                          borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '0.5px solid rgba(0,0,0,0.4)',
                          cursor: 'pointer'
                        }}
                        onClick={() => setTaxesPopoverOpen(!taxesPopoverOpen)}
                      >
                        <span style={{ color: activeColorStyle.borderColor, textDecoration: 'underline', fontWeight: 600 }}>Taxes:</span>
                        <span style={{ color: getTextColor() }}>{formatPriceWithDollar(worksheetData?.washout?.taxes)}</span>
                      </div>

                      {/* Taxes Popover */}
                      <Popover
                        placement="bottom"
                        isOpen={taxesPopoverOpen}
                        target="taxes-popover-target"
                        toggle={() => setTaxesPopoverOpen(!taxesPopoverOpen)}
                        trigger="click"
                        className={classNames("shadow-lg border-0 taxes-custom-popover", popoverTheme.modifier)}
                        style={{ maxWidth: 'none' }}
                      >
                        <div style={{ width: 'min(500px, 90vw)', backgroundColor: popoverTheme.bg, overflow: 'hidden' }}>
                          <PopoverHeader
                            tag="h4"
                            className="border-bottom-0 pt-3 pb-2 px-3"
                            style={{ backgroundColor: popoverTheme.headerBg, color: popoverTheme.text }}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <span style={{ fontWeight: 700, fontSize: '1.2rem' }}>Taxes</span>
                              <span style={{ fontWeight: 700, fontSize: '1.2rem' }}>{formatPriceWithDollar(worksheetData?.washout?.taxes)}</span>
                            </div>
                          </PopoverHeader>
                          <PopoverBody className="p-0" style={{ maxHeight: '400px', overflowY: 'auto', backgroundColor: popoverTheme.bg }}>
                            {showTaxForm ? (
                              <div className="px-3 pb-3">
                                <FormGroup>
                                  <Label style={{ fontSize: 13, fontWeight: 600, color: popoverTheme.label }}>Tax Package</Label>
                                  <Select
                                    styles={popoverSelectStyles}
                                    menuPortalTarget={document.body}
                                    className="react-select"
                                    value={{ label: "None Selected", value: "None Selected" }}
                                    options={[{ label: "None Selected", value: "None Selected" }]}
                                    isDisabled={true}
                                  />
                                </FormGroup>
                                <Row>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label style={{ fontSize: 13, fontWeight: 600, color: popoverTheme.label }}>Calc Type</Label>
                                      <Select
                                        styles={popoverSelectStyles}
                                        menuPortalTarget={document.body}
                                        className="react-select"
                                        value={{ label: currentTax.calc_type, value: currentTax.calc_type }}
                                        options={[
                                          "Registration State",
                                          "Contract State"
                                        ].map(opt => ({ label: opt, value: opt }))}
                                        onChange={(option) => setCurrentTax({ ...currentTax, calc_type: option.value })}
                                      />
                                    </FormGroup>
                                  </Col>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label style={{ fontSize: 13, fontWeight: 600, color: popoverTheme.label }}>Category</Label>
                                      <Select
                                        styles={popoverSelectStyles}
                                        menuPortalTarget={document.body}
                                        className="react-select"
                                        value={{ label: currentTax.category, value: currentTax.category }}
                                        options={[
                                          "City Sales Tax",
                                          "County Sales Tax",
                                          "Inventory Tax",
                                          "State Sales Tax"
                                        ].map(opt => ({ label: opt, value: opt }))}
                                        onChange={(option) => setCurrentTax({ ...currentTax, category: option.value })}
                                      />
                                    </FormGroup>
                                  </Col>
                                </Row>

                                <Row>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label style={{ fontSize: 13, fontWeight: 600, color: popoverTheme.label }}>Description</Label>
                                      <Input
                                        className="form-control"
                                        value={currentTax.description}
                                        onChange={(e) => setCurrentTax({ ...currentTax, description: e.target.value })}
                                        style={popoverInputStyle}
                                      />
                                    </FormGroup>
                                  </Col>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label style={{ fontSize: 13, fontWeight: 600, color: popoverTheme.label }}>Tax Variable</Label>
                                      <Select
                                        styles={popoverSelectStyles}
                                        menuPortalTarget={document.body}
                                        className="react-select"
                                        value={{ label: currentTax.tax_variable, value: currentTax.tax_variable }}
                                        options={[
                                          "Flat Tax - No Tax Variable", "SalesPrice", "Document Fee", "Service Contract",
                                          "SalesPrice - TradeIn", "SalesPrice + Document Fee - TradeIn",
                                          "SalesPrice + Document Fee + Service Contract - TradeIn",
                                          "SalesPrice + Document Fee",
                                          "SalesPrice + Document Fee + Business Tax - TradeIn",
                                          "SalesPrice - TradeIn - DealerRebate",
                                          "SalesPrice + Service Contract - TradeIn",
                                          "SalesPrice + Document Fee + Service Contract + TotalOtherFees",
                                          "SalesPrice + Service Contract - TradeIn - DealerRebate",
                                          "SalesPrice + Document Fee - DealerRebate",
                                          "SalesPrice + Document Fee + Business Tax + Service Contract - TradeIn",
                                          "Amount Financed (Doc Stamp Only)",
                                          "SalesPrice + Service Contract + GAP - TradeIn - ManfRebate",
                                          "SalesPrice + Document Fee + Service Contract + GAP",
                                          "SalesPrice + DocFee + TheftDevice + SurfaceProtection + PollutionControlFee",
                                          "SalesPrice + Pre-Delivery + Service Contract – Trade In",
                                          "SalesPrice + Document Fee + Service Contract + GAP - TradeIn",
                                          "SalesPrice + Document Fee + Service Contract + GAP + TotalOtherFees",
                                          "SalesPrice + Document Fee + InspectionFee",
                                          "SalesPrice + TotalFees + Service Contract - TradeIn",
                                          "SalesPrice + Pre-Delivery – Trade In",
                                          "SalesPrice + Document Fee + InspectionFee - Trade In",
                                          "SalesPrice + Document Fee + Pre-Delivery – Trade In",
                                          "SalesPrice + Document Fee + Pre-Delivery",
                                          "SalesPrice - TradeIn - ManfRebate",
                                          "SalesPrice + Pre-Delivery + Document Fee + Service Contract + GAP - TradeIn",
                                          "TheftDeterrent",
                                          "SalesPrice + DocumentFee + TheftDeterrent - TradeIn",
                                          "SalesPrice + Pre-Delivery + Service Contract + Electronic Filing Fee – Trade In",
                                          "SalesPrice + DocumentFee + Electronic Filing Fee – Trade In",
                                          "SalesPrice + Pre-Delivery + DocFee + EFiling Fee + Service Contract + GAP - TradeIn",
                                          "SalesPrice + Document Fee - TradeIn 1 Only",
                                          "SalesPrice + Document Fee + Electronic Filing Fee + Service Contract - TradeIn",
                                          "SalesPrice + TheftDeterrent - TradeIn",
                                          "SalesPrice + Pre-Delivery + Service Contract + GAP + EFiling Fee - Trade In",
                                          "SalesPrice + DocumentFee + Service Contract + Gap + TotalOtherFees + TheftDeterrent",
                                          "Gap",
                                          "SalesPrice + DocumentFee + TheftDeterrent",
                                          "SalesPrice + Gap",
                                          "SalesPrice + DocumentFee + InspectionFee + Electronic FilingFee - TradeIn",
                                          "Web - GA Ad Valorem Tax Call",
                                          "SalesPrice + Service Contract + GAP + InspectionFee - Trade In",
                                          "SalesPrice + InspectionFee",
                                          "SalesPrice + DocumentFee + LicenseFee - TradeIn",
                                          "SalesPrice + ServiceContract + TheftDeterrent + Warranty - TradeIn",
                                          "SalesPrice + FreightCharge + DocumentFee + Service Contract + GAP - TradeIn",
                                          "SalesPrice + DocumentFee + Service Contract + GAP + TheftDeterrent - TradeIn",
                                          "SalesPrice + FreightCharge + DocumentFee - TradeIn",
                                          "Smog Certification Fee",
                                          "Smog Exemption Fee",
                                          "Credit Life",
                                          "Credit Disability",
                                          "Unemployment Ins",
                                          "Property Insurance",
                                          "Optional VSI",
                                          "Road Hazard",
                                          "Service Contract",
                                          "Debt Cancellation",
                                          "Tire and Wheel",
                                          "GPS",
                                          "Surface Protection",
                                          "Window Etch",
                                          "Maintenance",
                                          "Limited Warranty",
                                          "Rust Proofing",
                                          "Dent And Ding",
                                          "Roadside Assistance",
                                          "Windshield Protection",
                                          "Key Replacement",
                                          "Service Fee",
                                          "Transportation Fee",
                                          "FL Doc Stamp Tax",
                                          "SalesPrice + DocumentFee + Electronic Filing Fee",
                                          "Inspection Fee",
                                          "Dealer Rebate",
                                          "Theft Device",
                                          "Pollution Control Fee",
                                          "Freight Charge",
                                          "Electronic Filing Fee",
                                          "PreDelivery Fee",
                                          "Property Assessment Fee",
                                          "Temporary Permit Fee",
                                          "Lien Fee",
                                          "Dealer Admin Fee",
                                          "Trauma Care Fee",
                                          "Mail-In Filing Fee",
                                          "Carrier Class Fee",
                                          "Messenger Fee",
                                          "Notary Fee",
                                          "County Local Use Fee",
                                          "Wheelage Tax",
                                          "Public Safety Vehicle Fee",
                                          "Transfer Tax",
                                          "Filing Fee",
                                          "Transit Tax",
                                          "Private Tag Agency Fee",
                                          "Uninsured Motorist Fee",
                                          "Convenience Fee",
                                          "Drivers Education Fee",
                                          "Supplemental Title Fee",
                                          "Dealer Reassignment Fee",
                                          "Registration Fee"
                                        ].map((opt) => ({ label: opt, value: opt }))}
                                        onChange={(option) => setCurrentTax({ ...currentTax, tax_variable: option.value })}
                                      />
                                    </FormGroup>
                                  </Col>
                                </Row>

                                <Row>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label style={{ fontSize: 13, fontWeight: 600, color: popoverTheme.label }}>Tax Rate</Label>
                                      <Input
                                        className="form-control"
                                        value={currentTax.tax_rate}
                                        onChange={(e) => setCurrentTax({ ...currentTax, tax_rate: e.target.value })}
                                        style={popoverInputStyle}
                                        placeholder="0.0000%"
                                      />
                                    </FormGroup>
                                  </Col>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label style={{ fontSize: 13, fontWeight: 600, color: popoverTheme.label }}>Amount</Label>
                                      <Input
                                        className="form-control"
                                        value={currentTax.amount}
                                        onChange={(e) => setCurrentTax({ ...currentTax, amount: e.target.value })}
                                        style={popoverInputStyle}
                                        placeholder="$0.00"
                                      />
                                    </FormGroup>
                                  </Col>
                                </Row>

                                <Row>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label style={{ fontSize: 13, fontWeight: 600, color: popoverTheme.label }}>Rate Indicator</Label>
                                      <Select
                                        styles={popoverSelectStyles}
                                        menuPortalTarget={document.body}
                                        className="react-select"
                                        value={{ label: currentTax.indicator, value: currentTax.indicator }}
                                        options={[
                                          "Fixed",
                                          "Dynamic"
                                        ].map(opt => ({ label: opt, value: opt }))}
                                        onChange={(option) => setCurrentTax({ ...currentTax, indicator: option.value })}
                                      />
                                    </FormGroup>
                                  </Col>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label style={{ fontSize: 13, fontWeight: 600, color: popoverTheme.label }}>Tax Fixed</Label>
                                      <Select
                                        styles={popoverSelectStyles}
                                        menuPortalTarget={document.body}
                                        className="react-select"
                                        value={{ label: currentTax.fixed, value: currentTax.fixed }}
                                        options={[
                                          { label: "Yes", value: "Yes" },
                                          { label: "No", value: "No" }
                                        ]}
                                        onChange={(option) => setCurrentTax({ ...currentTax, fixed: option.value })}
                                      />
                                    </FormGroup>
                                  </Col>
                                </Row>

                                <Row>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label style={{ fontSize: 13, fontWeight: 600, color: popoverTheme.label }}>Tax Beginning Range</Label>
                                      <Input
                                        className="form-control"
                                        style={popoverInputStyleRight}
                                        value={currentTax.beginning_range}
                                        onChange={(e) => setCurrentTax({ ...currentTax, beginning_range: e.target.value })}
                                        disabled={currentTax.fixed === 'Yes'}
                                      />
                                    </FormGroup>
                                  </Col>
                                  <Col md="6">
                                    <FormGroup>
                                      <Label style={{ fontSize: 13, fontWeight: 600, color: popoverTheme.label }}>Tax End Range</Label>
                                      <Input
                                        className="form-control"
                                        style={popoverInputStyleRight}
                                        value={currentTax.end_range}
                                        onChange={(e) => setCurrentTax({ ...currentTax, end_range: e.target.value })}
                                        disabled={currentTax.fixed === 'Yes'}
                                      />
                                    </FormGroup>
                                  </Col>
                                </Row>

                                <div className="mt-2 mb-4">
                                  <div style={{ fontWeight: 600, fontSize: 13, color: popoverTheme.label }}>Tax Base Amount:</div>
                                  <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4, color: popoverTheme.text }}>{formatPriceWithDollar(currentTax.tax_base || 0)}</div>
                                </div>

                                <div className="d-flex justify-content-between">
                                  <Button
                                    outline
                                    color="secondary"
                                    style={{ width: '48%', textTransform: 'uppercase', fontWeight: 600, fontSize: '11px', whiteSpace: 'nowrap', padding: '8px 0' }}
                                    onClick={() => setShowTaxForm(false)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    style={{
                                      width: '48%',
                                      background: activeColorStyle.color,
                                      border: 'none',
                                      textTransform: 'uppercase',
                                      fontWeight: 600,
                                      fontSize: '11px',
                                      whiteSpace: 'nowrap',
                                      padding: '8px 0'
                                    }}
                                    onClick={() => handleSaveTax()}
                                  >
                                    Confirm
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="px-3 pb-3">
                                <div className="d-flex justify-content-between font-weight-bold mb-2 text-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  <span style={{}}>Category</span>
                                  <span style={{}}>Amount</span>
                                </div>
                                <div className="mb-3">
                                  {worksheetData?.taxAndFee && worksheetData.taxAndFee.length > 0 ? (
                                    worksheetData.taxAndFee
                                      .filter(item => item.type === 'tax')
                                      .map((tax, index) => (
                                        <div key={getTaxFeeRowKey(tax) || index} className="mb-3" style={{ borderBottom: `1px solid ${popoverTheme.rowBorder}`, paddingBottom: '12px' }}>
                                          <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center">
                                              <span style={{ color: activeColorStyle.borderColor, fontSize: 14, fontWeight: 600 }}>{tax.category || tax.description}</span>
                                              {!isDisabled && (
                                                <>
                                                  <i
                                                    className="fa fa-edit ml-2 text-muted"
                                                    style={{ fontSize: 12, cursor: 'pointer' }}
                                                    onClick={() => {
                                                      setCurrentTax({
                                                        description: tax.description || "",
                                                        category: tax.category || "City Sales Tax",
                                                        amount: formatPriceWithDollar(tax.amount || 0),
                                                        calc_type: tax.calc_type || "Registration State",
                                                        tax_variable: tax.tax_variable || "SalesPrice",
                                                        tax_rate: tax.tax_rate ? `${Number(tax.tax_rate).toFixed(4)}` : "0.0000",
                                                        indicator: tax.indicator || 'Fixed',
                                                        fixed: tax.fixed ? 'Yes' : 'No',
                                                        beginning_range: formatPriceWithDollar(tax.beginning_range || 0),
                                                        end_range: formatPriceWithDollar(tax.end_range || 999999.99),
                                                        tax_base: tax.tax_base || 0,
                                                        local_id: tax.local_id,
                                                        id: tax.id,
                                                        documentId: tax.documentId
                                                      });
                                                      setShowTaxForm(true);
                                                    }}
                                                  ></i>
                                                  <i
                                                    className="fa fa-trash ml-2 text-muted"
                                                    style={{ fontSize: 12, cursor: 'pointer' }}
                                                    onClick={() => handleDeleteTax(tax)}
                                                  ></i>
                                                </>
                                              )}
                                            </div>
                                            <span style={{ fontWeight: 600, fontSize: 14, color: popoverTheme.text }}>{formatPriceWithDollar(tax.amount || '0')}</span>
                                          </div>

                                          {/* Detailed Breakdown */}
                                          <div style={{ fontSize: '11px', color: popoverTheme.detail, paddingLeft: '8px' }}>
                                            {tax.calc_type && <div className="d-flex justify-content-between"><span className="text-muted">Calc Type:</span> <span style={{ fontWeight: 500 }}>{tax.calc_type}</span></div>}
                                            {tax.tax_variable && <div className="d-flex justify-content-between"><span className="text-muted">Tax Variable:</span> <span style={{ fontWeight: 500 }}>{tax.tax_variable}</span></div>}
                                            {tax.tax_base && <div className="d-flex justify-content-between"><span className="text-muted">Tax Base:</span> <span style={{ fontWeight: 500 }}>{formatPriceWithDollar(tax.tax_base)}</span></div>}
                                            <div className="d-flex justify-content-between"><span className="text-muted">Tax Rate:</span> <span style={{ fontWeight: 500 }}>{Number(tax.tax_rate).toFixed(4)}%</span></div>
                                            <div className="d-flex justify-content-between"><span className="text-muted">Fixed:</span> <span style={{ fontWeight: 500 }}>{tax.fixed ? 'Yes' : 'No'}</span></div>
                                            <div className="d-flex justify-content-between"><span className="text-muted">Range:</span> <span style={{ fontWeight: 500 }}>{formatPriceWithDollar(tax.beginning_range || 0)} - {formatPriceWithDollar(tax.end_range || 999999.99)}</span></div>
                                          </div>
                                        </div>
                                      ))
                                  ) : (
                                    <div className="text-center text-muted" style={{ fontSize: 12, padding: 10 }}>No taxes added</div>
                                  )}
                                </div>
                                {!isDisabled && (
                                  <Button
                                    outline
                                    color="info"
                                    size="sm"
                                    block
                                    className="mt-3 mb-2 font-weight-bold"
                                    style={{ borderStyle: 'dashed', borderWidth: '2px', textTransform: 'uppercase', fontSize: 12 }}
                                    onClick={() => {
                                      setCurrentTax({
                                        description: "",
                                        category: "City Sales Tax",
                                        amount: "",
                                        calc_type: "Registration State",
                                        tax_variable: "SalesPrice",
                                        tax_rate: "0.00%",
                                        indicator: "Fixed",
                                        fixed: "Yes",
                                        beginning_range: "0.00",
                                        end_range: "999,999.99",
                                        tax_base: "0.00",
                                        id: null,
                                        local_id: null
                                      });
                                      setShowTaxForm(true);
                                    }}
                                  >
                                    + Add Tax
                                  </Button>
                                )}
                              </div>
                            )}
                          </PopoverBody>
                        </div>
                      </Popover>

                      {formValues.type !== "wholesale" && (
                        <div className="d-flex justify-content-between mb-2" style={{ borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '0.5px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor() }}>After Market:</span><span style={{ color: getTextColor() }}>{formatPriceWithDollar(worksheetData?.washout?.after_market)}</span></div>
                      )}
                      <div className="d-flex justify-content-between mb-2" style={{ borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '0.5px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor() }}>Accessories:</span><span style={{ color: getTextColor() }}>{formatPriceWithDollar(worksheetData?.washout?.accessory_prices)}</span></div>
                      <div className="d-flex justify-content-between mb-2" style={{ borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '0.5px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor() }}>Total Down:</span><span style={{ color: getTextColor() }}>{formatPriceWithDollar(worksheetData?.washout?.total_down)}</span></div>
                      <div className="d-flex justify-content-between mb-2" style={{ fontWeight: 700, borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '0.5px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor() }}>Total Sale:</span><span style={{ color: getTextColor() }}>{formatPriceWithDollar(worksheetData?.washout?.total_sale)}</span></div>
                      {(formValues.type == "cash" || formValues.type == "wholesale") && <div className="d-flex justify-content-between mb-2" ><span style={{ color: getTextColor() }}>Unpaid Balance:</span><span style={{ color: getTextColor() }}>{formatPriceWithDollar(worksheetData?.unpaid_balance)}</span></div>}
                    </Col>
                    {formValues.type !== "wholesale" && (
                      <Col md={formValues.type === "cash" ? "12" : "6"}>
                        <CardTitle tag="h5" className="mb-3">Finance Summary</CardTitle>
                        <div className="d-flex justify-content-between mb-2" style={{ fontWeight: 700, borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '0.5px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor() }}>Total Sale:</span><span style={{ color: getTextColor() }}>{formatPriceWithDollar(worksheetData?.washout?.total_sale)}</span></div>
                        <div className="d-flex justify-content-between mb-2" style={{ borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '0.5px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor() }}>Total Down:</span><span style={{ color: getTextColor() }}>{formatPriceWithDollar(worksheetData?.washout?.total_down)}</span></div>
                        <div className="d-flex justify-content-between mb-2" style={{ borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '0.5px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor() }}>Amount Financed:</span><span style={{ color: getTextColor() }}>{formatPriceWithDollar(worksheetData?.washout?.amount_financed)}</span></div>
                        <div className="d-flex justify-content-between mb-2" style={{ borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '0.5px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor() }}>Finance Charge:</span><span style={{ color: getTextColor() }}>{formatPriceWithDollar(worksheetData?.washout?.finance_charge)}</span></div>
                        <div className="d-flex justify-content-between mb-2" style={{ borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '0.5px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor() }}>APR:</span><span style={{ color: getTextColor() }}>{worksheetData?.washout?.apr?.toFixed(4)}</span></div>
                        <div className="d-flex justify-content-between mb-2" style={{ fontWeight: 700, borderBottom: currentTheme ? '0.5px solid rgba(255,255,255,0.4)' : '0.5px solid rgba(0,0,0,0.4)' }}><span style={{ color: getTextColor() }}>Total Payments:</span><span style={{ color: getTextColor() }}>{formatPriceWithDollar(worksheetData?.washout?.total_payments)}</span></div>
                      </Col>
                    )}
                  </Row>

                </CardBody>
              </Card >
            </Col >
          </Row >
        </Form >
      }

    </div>
  );
});

export default WorksheetForm;


